import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PlanningInsights from './components/PlanningInsights';
import { notify, confirmDialog } from './ui/notify';
import { apiCall } from './config';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getStationName } from './utils/stationMapping';
import ClaimDeclineModal from './components/ClaimDeclineModal';
// Debug-Ausgaben stumm (Philipp, 25.09.2026: Schichtplanung lud bei offener Konsole spürbar langsam – pro Neuzeichnen
// wurden hunderte Zeilen samt kompletter Datenlisten geloggt). Fehler/Warnungen bleiben. Zum Debuggen:
// localStorage.setItem('debugLogs', '1') + neu laden.
// eslint-disable-next-line no-shadow, no-redeclare
const console = (() => { let on = false; try { on = window.localStorage.getItem('debugLogs') === '1'; } catch (_) { on = false; } return on ? window.console : { ...window.console, log: () => {}, debug: () => {}, info: () => {} }; })();


const ItemTypes = {
  EMPLOYEE: 'employee'
};

// Schlüssel für die Kassenkraft-Zuweisungen. Laden, Lesen und Speichern MÜSSEN
// dieselben Funktionen benutzen – ein abweichender Schlüssel war der Grund,
// warum die Auswahl nach dem Neuladen verschwand.
const stationKey = (s) => String(s || '').toUpperCase();
const cashierKey = (userId, startTime) => `${userId}-${startTime}`;

// Resizable Employee Bar Component
function ResizableEmployeeBar({ 
  assignment, 
  availabilityData, 
  onUpdate, 
  onRemove, 
  onToggleLock, 
  timeToPosition, 
  durationToWidth, 
  employeeColorStyle, 
  isCashier, 
  calculateWorkingHours,
  timeScale,
  highlightedEmployeeId = null,
  onHighlight = null
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [resizeMode, setResizeMode] = useState(null);
  // Live-Vorschau in Pixeln während des Ziehens ({left,width}) – null = normale Prop-Position.
  const [drag, setDrag] = useState(null);
  // Alle Werte, die der mousemove/mouseup-Handler braucht, in einem Ref halten – so
  // gibt es KEINE veralteten Closures (früher sprang die Schicht wegen stale State).
  const dragRef = useRef(null);

  const SLOT_PX = 48; // 48px pro 30-Minuten-Slot
  const MIN_SLOTS_PX = SLOT_PX; // Mindestlänge einer Schicht: 30 Min

  const startPos = timeToPosition(assignment.start_time);
  const width = durationToWidth(assignment.start_time, assignment.end_time);

  // Während des Ziehens die Live-Pixelposition rendern, sonst die aus den Props.
  const displayLeft = drag ? drag.left : startPos;
  const displayWidth = drag ? drag.width : width;

  // Fallback für fehlende Farbdefinition
  const colorStyle = employeeColorStyle || { backgroundColor: '#007bff', color: 'white' };
  
  // Prüfe ob dieser Mitarbeiter hervorgehoben werden soll
  const isHighlighted = highlightedEmployeeId === assignment.user_id;
  
  // Prüfe ob die Zuweisung gesperrt ist
  const isLocked = assignment.locked === 1 || assignment.locked === true;

  const positionToTime = (position) => {
    // Runde auf 30-Minuten-Raster (kompakt: 48px pro Slot)
    const index = Math.max(0, Math.round(position / 48));

    // Innerhalb der sichtbaren Slots: direkt aus der Skala.
    if (index < timeScale.length) return timeScale[index];

    // Genau am rechten Rand (ein Slot hinter dem letzten sichtbaren) = Tagesende,
    // z.B. letzter Slot 24:30 → 01:00. So lässt sich bis 01:00 ziehen/resizen.
    return minutesToTime(timeToMinutes(timeScale[timeScale.length - 1]) + 30);
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;
    if (hours >= 0 && hours <= 2) totalMinutes += 24 * 60; // Nächster Tag (00:00–02:00); 05/06 Uhr = Frühmorgen
    return totalMinutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60) % 24;
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

  const handleMouseDown = (e, mode) => {
    // Blockiere Interaktionen für gesperrte Assignments
    if (isLocked) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Diesen Mitarbeiter links in „Geplante Mitarbeiter" markieren, solange man ihn
    // zieht/bearbeitet – so sieht man die Zusatzinfos (Partner-Wünsche etc.).
    if (onHighlight) onHighlight(assignment.user_id);

    // Bezugsrahmen EINMAL fixieren (nicht pro Move neu ermitteln → kein Versatz beim
    // vertikalen Ziehen über andere Stationen).
    const container = e.target.closest('.timeline-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX - rect.left;

    const origLeft = startPos;
    const origWidth = width;
    const origRight = origLeft + origWidth;
    // Rechte Kante der Timeline = spätestes Ende (z.B. 01:00). timelineWidth ist
    // timeScale.length * 48, also die Breite des Containers – hier NICHT length-1,
    // sonst ließe sich nur bis zum vorletzten Slot (24:30) ziehen.
    const maxPx = timeScale.length * SLOT_PX;

    // Verfügbarkeits-Grenzen (in Pixeln) vorab berechnen, damit die Vorschau live
    // an der Verfügbarkeit stoppt. Auf die Timeline klemmen, damit man eine Schicht
    // nicht über den rechten Rand hinausschieben kann (sonst wandern die Buttons ins
    // Unsichtbare außerhalb des Containers).
    const availability = availabilityData.find(a => a.user_id === assignment.user_id);
    const availLeftPx = (availability && availability.start_time) ? clamp(timeToPosition(availability.start_time), 0, maxPx) : null;
    const availRightPx = (availability && availability.end_time) ? clamp(timeToPosition(availability.end_time), 0, maxPx) : null;

    dragRef.current = {
      mode, rect, origLeft, origWidth, origRight, maxPx,
      grabOffset: mode === 'move' ? (startX - origLeft) : 0,
      availLeftPx, availRightPx,
      left: origLeft, width: origWidth, // zuletzt berechnete Live-Geometrie
    };

    setResizeMode(mode);
    setIsDragging(true);
    setDrag({ left: origLeft, width: origWidth });

    const handleMouseMove = (moveEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const currentX = moveEvent.clientX - d.rect.left;
      let left = d.left, w = d.width;

      if (d.mode === 'move') {
        left = currentX - d.grabOffset;
        const loLeft = d.availLeftPx != null ? d.availLeftPx : 0;
        const hiLeft = (d.availRightPx != null ? d.availRightPx : d.maxPx) - d.origWidth;
        left = clamp(left, loLeft, Math.max(loLeft, hiLeft));
        w = d.origWidth;
      } else if (d.mode === 'start') {
        const lo = d.availLeftPx != null ? d.availLeftPx : 0;
        left = clamp(currentX, lo, d.origRight - MIN_SLOTS_PX);
        w = d.origRight - left;
      } else if (d.mode === 'end') {
        const hi = d.availRightPx != null ? d.availRightPx : d.maxPx;
        const right = clamp(currentX, d.origLeft + MIN_SLOTS_PX, hi);
        left = d.origLeft;
        w = right - d.origLeft;
      }

      d.left = left; d.width = w; // fürs mouseup merken (kein stale State)
      setDrag({ left, width: w }); // nur lokale Vorschau – KEIN Server-Call
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      const d = dragRef.current;
      dragRef.current = null;
      setIsDragging(false);
      setResizeMode(null);
      if (!d) { setDrag(null); return; }

      // Live-Pixelgeometrie EINMAL auf das 30-Minuten-Raster snappen und in Zeiten
      // umrechnen (dieselben Verfügbarkeits-/Mindestdauer-Regeln wie zuvor).
      let newStartTime = assignment.start_time;
      let newEndTime = assignment.end_time;

      if (d.mode === 'move') {
        const originalDuration = timeToMinutes(assignment.end_time) - timeToMinutes(assignment.start_time);
        newStartTime = positionToTime(d.left);
        newEndTime = minutesToTime(timeToMinutes(newStartTime) + originalDuration);
        if (d.availLeftPx != null && d.availRightPx != null) {
          const availStart = timeToMinutes(positionToTime(d.availLeftPx));
          const availEnd = timeToMinutes(positionToTime(d.availRightPx));
          if (timeToMinutes(newStartTime) < availStart) {
            newStartTime = minutesToTime(availStart);
            newEndTime = minutesToTime(availStart + originalDuration);
          }
          if (timeToMinutes(newEndTime) > availEnd) {
            newEndTime = minutesToTime(availEnd);
            if (availEnd - timeToMinutes(newStartTime) < 30) newStartTime = minutesToTime(availEnd - 30);
          }
        }
      } else if (d.mode === 'start') {
        newStartTime = positionToTime(d.left);
        if (d.availLeftPx != null && timeToMinutes(newStartTime) < timeToMinutes(positionToTime(d.availLeftPx))) {
          newStartTime = positionToTime(d.availLeftPx);
        }
        if (timeToMinutes(assignment.end_time) - timeToMinutes(newStartTime) < 30) { setDrag(null); return; }
      } else if (d.mode === 'end') {
        newEndTime = positionToTime(d.left + d.width);
        if (d.availRightPx != null && timeToMinutes(newEndTime) > timeToMinutes(positionToTime(d.availRightPx))) {
          newEndTime = positionToTime(d.availRightPx);
        }
        if (timeToMinutes(newEndTime) - timeToMinutes(assignment.start_time) < 30) { setDrag(null); return; }
      }

      // Nichts geändert? Dann kein Server-Call, Vorschau einfach zurücknehmen.
      if (newStartTime === assignment.start_time && newEndTime === assignment.end_time) {
        setDrag(null);
        return;
      }

      // Vorschau exakt auf die Ziel-(Snap-)Position setzen und dort HALTEN, bis die
      // Aktualisierung durch ist – so gibt es kein Aufblitzen an der alten Stelle.
      setDrag({ left: timeToPosition(newStartTime), width: durationToWidth(newStartTime, newEndTime) });
      Promise.resolve(
        onUpdate(assignment.id, {
          date: assignment.date,
          user_id: assignment.user_id,
          station_id: assignment.station_id,
          start_time: newStartTime,
          end_time: newEndTime,
        })
      ).finally(() => setDrag(null));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      className={`position-absolute rounded d-flex align-items-center px-2 shadow-sm ${
        isCashier ? 'border border-warning border-2' : ''
      } ${isDragging ? 'opacity-75' : ''} ${isHighlighted ? 'border-primary border-3' : ''}`}
      style={{
        left: `${displayLeft}px`,
        width: `${displayWidth}px`,
        height: isCashier ? '20px' : '18px', // Kassenkräfte etwas dicker (18px + 2px Border)
        fontSize: '10px', // Reduziert von 11px auf 10px
        cursor: isLocked ? 'not-allowed' : (resizeMode ? (resizeMode === 'move' ? 'move' : 'ew-resize') : 'move'),
        minWidth: '60px', // Mindestbreite beibehalten
        zIndex: isDragging || isHighlighted ? 1000 : 1,
        userSelect: 'none',
        backgroundColor: isHighlighted ? '#007bff' : colorStyle.backgroundColor,
        color: isHighlighted ? 'white' : colorStyle.color,
        border: isHighlighted ? '3px solid #0d6efd' : (isCashier ? '2px solid #ffc107' : '1px solid rgba(0,0,0,0.1)'),
        boxShadow: isHighlighted ? '0 0 8px rgba(13, 110, 253, 0.8)' : (isCashier ? '0 0 4px rgba(255, 193, 7, 0.5)' : 'none'),
        transform: isHighlighted ? 'scale(1.02)' : 'none',
        // Beim Ziehen keine Animation → Block folgt dem Cursor 1:1 (kein Nachschwimmen).
        transition: isDragging ? 'none' : 'all 0.2s ease',
        opacity: isLocked ? 0.8 : 1,
        filter: isLocked ? 'grayscale(20%)' : 'none'
      }}
      title={`${assignment.employee_name}\n${assignment.start_time} - ${assignment.end_time}${isCashier ? '\n💰 Kassenkraft' : ''}${isHighlighted ? '\n🎯 Hervorgehoben' : ''}${isLocked ? '\n🔒 Gesperrt' : '\nZiehen: Verschieben | Ränder: Resize'}`}
      onMouseDown={(e) => handleMouseDown(e, 'move')}
    >
      {/* Resize Handle Left */}
      {!isLocked && (
        <div
          className="resize-handle-left"
          style={{
            position: 'absolute',
            left: '0',
            top: '0',
            width: '6px',
            height: '100%',
            cursor: 'ew-resize',
            backgroundColor: 'rgba(255,255,255,0.35)',
            borderRadius: '3px 0 0 3px',
            zIndex: 2
          }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
          title="Start-Zeit ändern"
        />
      )}

      {/* Content */}
      <div className="d-flex justify-content-between align-items-center w-100" style={{ pointerEvents: 'none' }}>
        <span className="text-truncate fw-medium" style={{ color: colorStyle.color }}>
          {isCashier && '💰 '}{assignment.employee_name}
        </span>
        {width > 100 && (
          <span className="small ms-1" style={{ 
            fontSize: '9px', 
            color: colorStyle.color,
            opacity: 0.8 
          }}>
            {calculateWorkingHours(assignment.start_time, assignment.end_time)}
          </span>
        )}
        <div className="d-flex align-items-center" style={{ pointerEvents: 'auto' }}>
          {/* Lock/Unlock Button */}
          <button
            className="btn btn-sm btn-outline-light me-1 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleLock(assignment.id, !isLocked);
            }}
            style={{ 
              fontSize: '10px', 
              width: '18px', 
              height: '18px', 
              lineHeight: '1',
              backgroundColor: isLocked ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.2)',
              borderColor: colorStyle.color,
              color: colorStyle.color
            }}
            title={isLocked ? "Entsperren (erlaubt Bearbeitung)" : "Sperren (verhindert Bearbeitung)"}
          >
            {isLocked ? '🔒' : '🔓'}
          </button>
          {/* Remove Button */}
          <button
            className="btn btn-sm btn-outline-light p-0"
            onClick={(e) => {
              e.stopPropagation();
              if (isLocked) {
                notify('Diese Zuweisung ist gesperrt und kann nicht gelöscht werden. Entsperren Sie sie zuerst.');
                return;
              }
              onRemove(assignment);
            }}
            style={{ 
              fontSize: '10px', 
              width: '18px', 
              height: '18px', 
              lineHeight: '1',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderColor: colorStyle.color,
              color: colorStyle.color,
              opacity: isLocked ? 0.5 : 1
            }}
            title={isLocked ? "Kann nicht gelöscht werden (gesperrt)" : "Entfernen"}
          >
            ×
          </button>
        </div>
      </div>

      {/* Resize Handle Right */}
      {!isLocked && (
        <div
          className="resize-handle-right"
          style={{
            position: 'absolute',
            right: '0',
            top: '0',
            width: '6px',
            height: '100%',
            cursor: 'ew-resize',
            backgroundColor: 'rgba(255,255,255,0.35)',
            borderRadius: '0 3px 3px 0',
            zIndex: 2
          }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
          title="End-Zeit ändern"
        />
      )}
    </div>
  );
}

// Partner Group Container Component
function PartnerGroupContainer({
  group,
  suggestions = [],
  onAssign,
  availabilityData = [],
  plannedAssignments = [],
  isPlanned = false,
  stations = [],
  highlightedEmployeeId = null,
  onEmployeeClick = null,
  stationStats = [],
  prevAssignmentsByUser = {},
  fairnessByUser = {}
}) {
  // Partner-Gruppen-Farbpalette
  const partnerColors = [
    { bg: '#fff5f5', border: '#fc8181', accent: '#e53e3e' }, // Rot
    { bg: '#f0f9ff', border: '#63b3ed', accent: '#3182ce' }, // Blau  
    { bg: '#f0fff4', border: '#68d391', accent: '#38a169' }, // Grün
    { bg: '#fffaf0', border: '#f6ad55', accent: '#dd6b20' }, // Orange
    { bg: '#faf5ff', border: '#b794f6', accent: '#805ad5' }, // Lila
    { bg: '#f0fdfa', border: '#4fd1c7', accent: '#319795' }, // Türkis
    { bg: '#fffbf0', border: '#f7b733', accent: '#d69e2e' }, // Gelb
    { bg: '#f7fafc', border: '#a0aec0', accent: '#4a5568' }, // Grau
    { bg: '#fef7f7', border: '#f56565', accent: '#c53030' }, // Pink-Rot
    { bg: '#f9fffe', border: '#81e6d9', accent: '#2c7a7b' }, // Aqua
  ];
  
  // Konsistente Farbzuweisung basierend auf der ersten Mitglieder-ID
  const colorIndex = (group.members[0]?.id || 0) % partnerColors.length;
  const groupColor = partnerColors[colorIndex];
  
  return (
    <div 
      className="mb-3 p-3 border rounded-3 position-relative"
      style={{ 
        borderColor: groupColor.border,
        borderWidth: '2px',
        backgroundColor: groupColor.bg,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      {/* Partner-Gruppen-Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center">
          <span 
            className="badge rounded-pill px-3 py-2 me-2"
            style={{ 
              backgroundColor: groupColor.accent, 
              color: 'white',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            🤝 Partner-Team
          </span>
          <strong style={{ color: groupColor.accent, fontSize: '14px' }}>
            {group.name}
          </strong>
          {group.canWorkCash && (
            <span 
              className="badge bg-warning text-dark ms-2" 
              title="Mindestens ein Mitglied kann Kasse bedienen"
            >
              💰 Kasse
            </span>
          )}
        </div>
        <small className="text-muted">
          {group.members.length} Mitglied{group.members.length > 1 ? 'er' : ''}
        </small>
      </div>
      
      {/* Partner-Verbindungslinien (visueller Hinweis) */}
      <div 
        className="position-absolute top-0 start-0 w-100 h-100 rounded-3"
        style={{
          background: `linear-gradient(45deg, transparent 45%, ${groupColor.border}40 49%, ${groupColor.border}40 51%, transparent 55%)`,
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
      
      {/* Einzelne Mitarbeiter-Karten im Container */}
      <div className="position-relative" style={{ zIndex: 1 }}>
        {group.members.map((member, index) => (
          <div 
            key={member.id}
            className="position-relative"
            style={{
              marginBottom: index < group.members.length - 1 ? '12px' : '0'
            }}
          >
            {/* Partner-Verbindungslinie zwischen Karten */}
            {index < group.members.length - 1 && (
              <div 
                className="position-absolute"
                style={{
                  left: '50%',
                  bottom: '-6px',
                  width: '3px',
                  height: '12px',
                  backgroundColor: groupColor.accent,
                  transform: 'translateX(-50%)',
                  zIndex: 2
                }}
              />
            )}
            <div
              className="partner-card-wrapper"
              style={{
                borderRadius: '8px',
                border: `1px solid ${groupColor.border}`,
                backgroundColor: 'white',
                boxShadow: `0 1px 3px ${groupColor.border}30`
              }}
            >
              <EmployeeCard
                employee={member}
                suggestions={suggestions}
                onAssign={onAssign}
                availabilityData={availabilityData}
                plannedAssignments={plannedAssignments}
                isPlanned={isPlanned}
                stations={stations}
                highlightedEmployeeId={highlightedEmployeeId}
                onEmployeeClick={onEmployeeClick}
                isPartnerMember={true}
                partnerGroupColor={groupColor}
                stationStats={stationStats}
                prevAssignmentsByUser={prevAssignmentsByUser}
                          fairnessByUser={fairnessByUser}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Partner Group Card Component (für Drag-and-Drop des ganzen Teams)
function PartnerGroupCard({ 
  group, 
  suggestions = [], 
  onAssign, 
  availabilityData = [], 
  plannedAssignments = [], 
  isPlanned = false, 
  stations = [],
  highlightedEmployeeId = null,
  onEmployeeClick = null
}) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.EMPLOYEE,
    item: { employee: group }, // Verwende die Gruppe als Employee-Objekt
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  // Prüfe ob alle Mitglieder der Gruppe verfügbar sind
  const allMembersAvailable = group.members.every(member => {
    const availability = availabilityData.find(avail => avail.user_id === member.id);
    return availability && availability.is_available === 1;
  });

  return (
    <div
      ref={drag}
      className={`card mb-2 ${isDragging ? 'opacity-50' : ''} border-2`}
      style={{ 
        cursor: 'move', 
        borderColor: group.canWorkCash ? '#28a745' : '#007bff',
        borderStyle: 'solid',
        background: `linear-gradient(135deg, ${group.canWorkCash ? '#e8f5e8' : '#e3f2fd'}, white)`
      }}
    >
      <div className="card-body p-2">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="card-title mb-1 fw-bold" style={{ color: group.canWorkCash ? '#28a745' : '#007bff' }}>
              🤝 {group.name}
            </h6>
            <small className="text-muted">
              {group.canWorkCash && <span className="badge bg-success me-1">💰 Kasse</span>}
              <span className="badge bg-secondary">{group.members.length} Personen</span>
            </small>
            <div className="mt-1">
              {group.members.map((member, index) => (
                <small key={member.id} className="d-block text-muted">
                  • {member.name || `${member.firstname || ''} ${member.lastname || ''}`.trim() || `User ${member.id}`}
                </small>
              ))}
            </div>
          </div>
          {group.canWorkCash && (
            <div className="text-success">
              <i className="fas fa-cash-register fa-lg"></i>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Employee Card Component
function EmployeeCard({ 
  employee, 
  suggestions = [], 
  onAssign, 
  availabilityData = [], 
  plannedAssignments = [], 
  isPlanned = false, 
  stations = [],
  highlightedEmployeeId = null,
  onEmployeeClick = null,
  isPartnerMember = false,
  partnerGroupColor = null,
  hasPotentialPartners = false,
  stationStats = [],
  prevAssignmentsByUser = {},
  fairnessByUser = {}
}) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.EMPLOYEE,
    item: { employee },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const suggestionInfo = (suggestions || []).find(s => s.id === employee.id);
  const priorityClass = suggestionInfo ? 
    (suggestionInfo.priority === 'high' ? 'border-danger' : 
     suggestionInfo.priority === 'medium' ? 'border-warning' : 'border-info') : '';

  const availability = availabilityData.find(a => a.user_id === employee.id);
  
  // Finde geplante Zeiten für diesen Mitarbeiter
  const employeeAssignments = plannedAssignments.filter(a => a.user_id === employee.id);
  
  // Prüfe ob dieser Mitarbeiter hervorgehoben werden soll
  const isHighlighted = highlightedEmployeeId === employee.id;
  
  // Funktion um zu prüfen ob geplante Zeit innerhalb der Verfügbarkeit liegt
  const isTimeWithinAvailability = (startTime, endTime) => {
    if (!availability || !availability.start_time || !availability.end_time) return true;
    
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes;
      if (hours >= 0 && hours <= 2) totalMinutes += 24 * 60; // Nächster Tag (00:00–02:00); 05/06 Uhr = Frühmorgen
      return totalMinutes;
    };

    const availStart = timeToMinutes(availability.start_time);
    const availEnd = timeToMinutes(availability.end_time);
    const plannedStart = timeToMinutes(startTime);
    const plannedEnd = timeToMinutes(endTime);
    
    return plannedStart >= availStart && plannedEnd <= availEnd;
  };

  return (
    <div
      ref={drag}
      className={`card mb-2 ${priorityClass} ${isDragging ? 'opacity-50' : ''} ${isPlanned ? 'border-success bg-light' : ''} ${isHighlighted ? 'border-primary bg-primary bg-opacity-10' : ''}`}
      style={{ 
        cursor: 'move',
        boxShadow: isHighlighted ? '0 0 10px rgba(13, 110, 253, 0.5)' : 
                   (isPartnerMember && partnerGroupColor ? `0 0 8px ${partnerGroupColor.border}60` : undefined),
        borderColor: isPartnerMember && partnerGroupColor ? partnerGroupColor.border : undefined,
        borderWidth: isPartnerMember ? '2px' : undefined,
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={() => onEmployeeClick && onEmployeeClick(employee.id)}
    >
      {/* Partner-Indikator */}
      {isPartnerMember && partnerGroupColor && (
        <div 
          className="position-absolute top-0 start-0"
          style={{
            width: '4px',
            height: '100%',
            backgroundColor: partnerGroupColor.accent,
            zIndex: 1
          }}
        />
      )}
      
      <div className="card-body p-2" style={{ position: 'relative', zIndex: 2 }}>
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-1">
              {isPartnerMember && (
                <span 
                  className="badge rounded-pill me-1"
                  style={{ 
                    backgroundColor: partnerGroupColor.accent,
                    color: 'white',
                    fontSize: '10px'
                  }}
                  title="Partner-Team Mitglied"
                >
                  🤝
                </span>
              )}
              {!isPartnerMember && hasPotentialPartners && (
                <span 
                  className="badge bg-secondary rounded-pill me-1"
                  style={{ fontSize: '10px' }}
                  title="Hat Partner-Wünsche (Partner nicht verfügbar)"
                >
                  💭
                </span>
              )}
              <h6 className="card-title mb-0">{employee.name}</h6>
              {availability && availability.can_work_cash_register === 1 && (
                <span className="badge bg-success" title="Kassenkraft">
                  💰
                </span>
              )}
            </div>
            
            {/* Partner-Präferenzen Anzeige */}
            {availability && availability.partners && availability.partners.length > 0 && (
              <div className="text-muted small mb-1">
                <i className="bi bi-people-fill me-1"></i>
                <span className="fw-medium">Partner-Wünsche:</span>
                <div className="mt-1">
                  {availability.partners.map((partner, index) => (
                    <span 
                      key={index} 
                      className="badge bg-info me-1" 
                      style={{ fontSize: '10px' }}
                      title={`Priorität ${partner.priority}`}
                    >
                      {partner.partner_name} (P{partner.priority})
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Station-Statistiken Anzeige */}
            {(() => {
              const userStats = stationStats.find(stat => stat.user_id === employee.id);
              return userStats && userStats.preferred_station ? (
                <div className="text-muted small mb-1">
                  <i className="bi bi-building me-1"></i>
                  <span className="fw-medium">Meist verwendet:</span>
                  <span 
                    className="badge bg-primary ms-1" 
                    style={{ fontSize: '10px' }}
                    title={`${userStats.preferred_station.total_stunden}h gesamt (${userStats.preferred_station.einsaetze} Einsätze, ${userStats.preferred_station.prozent_der_gesamtzeit}% der Arbeitszeit)`}
                  >
                    {userStats.preferred_station.station_id} ({userStats.preferred_station.total_stunden}h)
                  </span>
                </div>
              ) : null;
            })()}
            {/* Fairness: Verplan-Quote in % + Rang (1 = am stärksten benachteiligt,
                sollte als Nächstes eingeteilt werden). Farbe nach Quote. */}
            {(() => {
              const f = fairnessByUser[employee.id];
              if (!f || !f.avail_days) return null;
              const hours = Math.round((f.planned_minutes || 0) / 60);
              const cls = f.pct < 25 ? 'text-danger fw-medium'
                : f.pct < 50 ? 'text-warning fw-medium'
                : 'text-muted';
              const topRank = f.rank <= 5 && f.avail_days >= 5;
              return (
                <div className={`small mb-1 ${cls}`}
                  title={`In ${f.planned_days} von ${f.avail_days} verfügbaren Tagen eingeplant (${hours} Std). Rang ${f.rank} von ${f.total}: je niedriger, desto dringender einteilen.`}>
                  <span className="me-1">⚖️</span>
                  <span className="fw-bold">{f.pct} %</span> verplant ({f.planned_days}/{f.avail_days} Tage · {hours} h)
                  <span className={`badge ms-2 ${topRank ? 'bg-danger' : f.pct < 50 ? 'bg-warning text-dark' : 'bg-light text-dark border'}`} style={{ fontSize: '10px' }}>
                    Rang {f.rank}/{f.total}
                  </span>
                  {topRank && <span className="ms-1" title="Gehört zu den 5 am stärksten Benachteiligten – als Nächstes einteilen!">🔥</span>}
                </div>
              );
            })()}
            {availability && availability.start_time && availability.end_time && (
              <div className="text-muted small mb-1">
                <i className="bi bi-clock me-1"></i>
                <span className="fw-medium">
                  {availability.start_time} - {availability.end_time}
                </span>
                {/* Geplante Zeiten nebeneinander anzeigen */}
                {employeeAssignments.length > 0 && (
                  <span className="ms-3">
                    <i className="bi bi-calendar-check me-1"></i>
                    {employeeAssignments.map((assignment, index) => {
                      const isValid = isTimeWithinAvailability(assignment.start_time, assignment.end_time);
                      // Finde Station-Name
                      const station = stations.find(s => s.id === assignment.station);
                      const stationDisplayName = station ? station.name : getStationName(assignment.station);
                      return (
                        <span key={index} className={`badge ms-1 ${isValid ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '10px' }}>
                          {assignment.start_time} - {assignment.end_time} ({stationDisplayName})
                        </span>
                      );
                    })}
                  </span>
                )}
              </div>
            )}
            {/* Vortags-Schichten: verhindert 01:00-Ende + 07:00-Start am Folgetag */}
            {(() => {
              const prev = prevAssignmentsByUser[employee.id] || [];
              if (!prev.length) return null;
              const lateEnd = prev.some((a) => {
                const h = parseInt(String(a.end_time).split(':')[0], 10);
                return h >= 23 || h <= 2;
              });
              return (
                <div className={`small mb-1 ${lateEnd ? 'text-danger fw-medium' : 'text-muted'}`}>
                  <i className="bi bi-moon me-1"></i>
                  <span className="fw-medium">Vortag:</span>
                  {prev.map((a, i) => (
                    <span key={i} className="ms-1">
                      {a.start_time}–{a.end_time} ({getStationName(a.station_id || a.station)})
                    </span>
                  ))}
                  {lateEnd && <span className="ms-1" title="Spätes Schichtende am Vortag – Ruhezeit beachten!">⚠️</span>}
                </div>
              );
            })()}
            {availability && availability.notes && (
              <div className="text-info small">
                <i className="bi bi-info-circle me-1"></i>
                <span>{availability.notes}</span>
              </div>
            )}
          </div>
          {suggestionInfo && (
            <div>
              <span className={`badge ${
                suggestionInfo.priority === 'high' ? 'bg-danger' : 
                suggestionInfo.priority === 'medium' ? 'bg-warning' : 'bg-info'
              }`}>
                {suggestionInfo.reason}
              </span>
            </div>
          )}
        </div>
        {suggestionInfo && (
          <button
            className="btn btn-sm btn-outline-primary mt-1"
            onClick={() => onAssign(employee, suggestionInfo.station, suggestionInfo.time)}
          >
            Vorschlag übernehmen
          </button>
        )}
      </div>
    </div>
  );
}

// Cashier Assignment Bar Component - Neue Version mit klickbaren Schichtbalken
function CashierBar({ 
  station, 
  timeScale, 
  cashierAssignments, 
  getAvailableCashiers, 
  assignCashier, 
  employees,
  assignments,
  availabilityData,
  timeToPosition,
  durationToWidth,
  timeToMinutes
}) {
  // Debug-Ausgabe für Station
  console.log(`🔧 CashierBar rendered for station: "${station}"`);
  
  const timelineWidth = timeScale.length * 48;

  // Hole alle Kassenkraft-Schichten für diese Station
  const getCashierShifts = () => {
    // Debug-Ausgabe für G10
    if (station === 'G10') {
      console.log(`🔍 G10 DEBUG: getCashierShifts() called for station: ${station}`);
      console.log(`🔍 G10 DEBUG: Total assignments:`, assignments?.length || 0);
      console.log(`🔍 G10 DEBUG: availabilityData:`, availabilityData?.length || 0);
      console.log(`🔍 G10 DEBUG: All assignments:`, assignments);
      console.log(`🔍 G10 DEBUG: All availabilityData:`, availabilityData);
    }

    const result = assignments.filter(assignment => {
      // Filtere nur Assignments für diese Station - case-insensitive Vergleich
      const assignmentStation = assignment.station_id || assignment.station;
      const normalizedAssignmentStation = assignmentStation ? String(assignmentStation).toLowerCase() : '';
      const normalizedStation = station ? station.toLowerCase() : '';
      
      if (normalizedAssignmentStation !== normalizedStation) {
        if (station === 'G10') {
          console.log(`🔍 G10 DEBUG: Assignment ${assignment.user_id} filtered out - wrong station: ${assignment.station_id || assignment.station} vs ${station}`);
        }
        return false;
      }
      
      // Log für passende G10 Assignments
      if (station === 'G10') {
        console.log(`🔍 G10 DEBUG: Assignment ${assignment.user_id} MATCHES station: ${assignment.station_id || assignment.station} vs ${station}`);
      }
      
      // Prüfe ob der Mitarbeiter als Kassenkraft arbeiten kann
      const availability = availabilityData.find(avail => avail.user_id === assignment.user_id);
      
      // Debug-Ausgabe für Probleme
      if (station === 'G10') {
        console.log(`🔍 G10 DEBUG: Assignment ${assignment.user_id} (${assignment.employee_name}):`, {
          station_id: assignment.station_id,
          station: assignment.station,
          availability_found: !!availability,
          can_work_cash_register: availability?.can_work_cash_register,
          passed_filter: !!(availability && availability.can_work_cash_register === 1)
        });
      }
      
      return availability && availability.can_work_cash_register === 1;
    }).map(assignment => {
      const employee = employees.find(emp => emp.id === assignment.user_id);
      
      // Debug-Ausgabe für Namen-Zuordnung
      console.log(`🔍 Name mapping for assignment ${assignment.user_id}:`, {
        raw_assignment: assignment,
        found_employee: employee,
        assignment_has_firstname: !!assignment.firstname,
        assignment_has_lastname: !!assignment.lastname,
        assignment_has_user_name: !!assignment.user_name,
        assignment_has_employee_name: !!assignment.employee_name,
        employee_id_match: employee?.id === assignment.user_id
      });
      
      // Priorität: 1. API-Response Namen, 2. Employee Namen, 3. Fallback
      let resolvedName = '';
      if (assignment.user_name) {
        resolvedName = assignment.user_name;
      } else if (assignment.firstname && assignment.lastname) {
        resolvedName = `${assignment.firstname} ${assignment.lastname}`;
      } else if (employee?.name) {
        resolvedName = employee.name;
      } else if (employee?.firstname && employee?.lastname) {
        resolvedName = `${employee.firstname} ${employee.lastname}`;
      } else if (assignment.employee_name) {
        resolvedName = assignment.employee_name;
      } else {
        resolvedName = `User ${assignment.user_id}`;
      }
      
      console.log(`🔍 Resolved name for user ${assignment.user_id}: "${resolvedName}"`);
      
      return {
        ...assignment,
        employee_name: resolvedName,
        position: timeToPosition(assignment.start_time),
        width: durationToWidth(assignment.start_time, assignment.end_time)
      };
    });

    if (station === 'G10') {
      console.log(`🔍 G10 DEBUG: getCashierShifts result:`, result);
    }

    return result;
  };

  const cashierShifts = getCashierShifts();
  
  // Zusätzlicher Debug für G10
  if (station === 'G10') {
    console.log(`🔍 G10 DEBUG: Final cashierShifts length: ${cashierShifts.length}`);
    console.log(`🔍 G10 DEBUG: Will show "Keine Kassenkräfte" message: ${cashierShifts.length === 0}`);
  }

  // Prüfe ob eine Schicht als Kassenkraft ausgewählt ist
  const isSelectedAsCashier = (assignment) => {
    const stationAssignments = cashierAssignments[stationKey(station)] || {};
    return stationAssignments[cashierKey(assignment.user_id, assignment.start_time)] !== undefined;
  };

  // Wähle/Deselektiere eine Kassenkraft-Schicht
  const toggleCashierSelection = (assignment) => {
    const timeSlot = cashierKey(assignment.user_id, assignment.start_time);
    
    if (isSelectedAsCashier(assignment)) {
      // Entferne die Auswahl (ID mitgeben, damit das Flag auch in der DB fällt)
      assignCashier(station, timeSlot, undefined, assignment);
    } else {
      // Füge die Auswahl hinzu und handhabe Überlappungen
      const newSelection = {
        ...assignment,
        assignment_id: assignment.assignment_id || assignment.id,
        // Schichtzeiten separat merken: start_time/end_time beschreiben ab jetzt
        // das KASSEN-Fenster und können bei einer Übergabe schrumpfen.
        shift_start: assignment.shift_start || assignment.start_time,
        shift_end: assignment.shift_end || assignment.end_time,
        selectedAt: Date.now() // Zeitstempel für "zuletzt ausgewählt"
      };
      assignCashier(station, timeSlot, newSelection);
      
      // Behandle Überlappungen mit anderen ausgewählten Kassenkräften
      handleOverlaps(assignment);
    }
  };

  // Behandle Überlappungen - intelligente Schichtaufteilung
  const handleOverlaps = (newAssignment) => {
    const newStart = timeToPosition(newAssignment.start_time);
    const newEnd = newStart + durationToWidth(newAssignment.start_time, newAssignment.end_time);
    
    // Finde alle anderen ausgewählten Kassenkräfte, die überlappen (nur in dieser Station)
    const stationAssignments = cashierAssignments[stationKey(station)] || {};
    Object.entries(stationAssignments).forEach(([timeSlot, existingAssignment]) => {
      if (!existingAssignment || timeSlot === cashierKey(newAssignment.user_id, newAssignment.start_time)) return;
      
      const existingStart = timeToPosition(existingAssignment.start_time);
      const existingEnd = existingStart + durationToWidth(existingAssignment.start_time, existingAssignment.end_time);
      
      // Prüfe auf Überlappung
      if (newStart < existingEnd && newEnd > existingStart) {
        // Bestimme Art der Überlappung und handle entsprechend
        if (newStart > existingStart && newStart < existingEnd) {
          // Neue Kassenkraft startet während bestehender Schicht
          // -> Teile die bestehende Schicht auf (sie endet wenn neue anfängt)
          const splitEndTime = newAssignment.start_time;
          
          // Erstelle eine geteilte Version der bestehenden Zuweisung
          const splitAssignment = {
            ...existingAssignment,
            end_time: splitEndTime,
            isSplit: true,
            originalEnd: existingAssignment.end_time
          };
          
          // Ersetze die alte Zuweisung mit der geteilten Version
          assignCashier(station, timeSlot, splitAssignment);
          
          console.log(`📊 Schicht geteilt: ${existingAssignment.employee_name} übernimmt Kasse bis ${splitEndTime}, dann übernimmt ${newAssignment.employee_name}`);
        } else if (newEnd < existingEnd && newEnd > existingStart) {
          // Neue Kassenkraft endet während bestehender Schicht
          // -> Bestehende Schicht startet erst nach Ende der neuen
          const splitStartTime = newAssignment.end_time;
          
          // Erstelle eine geteilte Version der bestehenden Zuweisung
          const splitAssignment = {
            ...existingAssignment,
            start_time: splitStartTime,
            isSplit: true,
            originalStart: existingAssignment.start_time
          };
          
          // Ersetze die alte Zuweisung mit der geteilten Version
          assignCashier(station, timeSlot, splitAssignment);
          
          console.log(`📊 Schicht geteilt: ${newAssignment.employee_name} übernimmt Kasse bis ${splitStartTime}, dann übernimmt ${existingAssignment.employee_name}`);
        } else {
          // Vollständige Überlappung - entferne die ältere Auswahl
          assignCashier(station, timeSlot, undefined, existingAssignment);
          console.log(`📊 Vollständige Überlappung: ${existingAssignment.employee_name} wird durch ${newAssignment.employee_name} ersetzt`);
        }
      }
    });
  };

  // Balken in BAHNEN verteilen: Überlappen sich zwei Kassenkräfte (typisch bei
  // der Übergabe), lagen sie vorher übereinander – der obere verdeckte den
  // unteren, der dadurch teils gar nicht mehr anklickbar war. Jeder Balken
  // bekommt jetzt die erste Bahn, in der er frei steht.
  const stationAssignmentsForLayout = cashierAssignments[stationKey(station)] || {};
  const balken = cashierShifts.map((shift, index) => {
    const gewaehlt = stationAssignmentsForLayout[cashierKey(shift.user_id, shift.start_time)];
    const von = gewaehlt?.start_time || shift.start_time;
    const bis = gewaehlt?.end_time || shift.end_time;
    return {
      shift, index, gewaehlt, von, bis,
      links: gewaehlt ? timeToPosition(von) : shift.position,
      breite: gewaehlt ? durationToWidth(von, bis) : shift.width,
    };
  }).sort((a, b) => a.links - b.links);

  const bahnEnde = [];   // rechter Rand des letzten Balkens je Bahn
  balken.forEach((b) => {
    let bahn = bahnEnde.findIndex((ende) => b.links >= ende - 0.5);
    if (bahn === -1) { bahn = bahnEnde.length; }
    bahnEnde[bahn] = b.links + b.breite;
    b.bahn = bahn;
  });
  const bahnen = Math.max(1, bahnEnde.length);
  const BAHN_HOEHE = 32;

  return (
    <div className="cashier-bar" style={{
      height: `${10 + bahnen * BAHN_HOEHE}px`,
      width: `${timelineWidth}px`,
      border: '1px solid #dee2e6',
      backgroundColor: '#f8f9fa',
      position: 'relative',
      marginBottom: '5px'
    }}>
      <div className="position-absolute" style={{
        left: '5px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '12px',
        fontWeight: 'bold',
        color: '#495057'
      }}>
        Kassenkraft:
      </div>

      {/* Zeige alle verfügbaren Kassenkraft-Schichten als klickbare Balken */}
      {balken.map(({ shift, index, gewaehlt, von, bis, links, breite, bahn }) => {
        const isSelected = isSelectedAsCashier(shift);
        const selectedAssignment = gewaehlt;

        // Verwende die geteilten Zeiten falls vorhanden
        const displayStartTime = von;
        const displayEndTime = bis;

        // Markiere geteilte Schichten visuell
        const isSplit = selectedAssignment?.isSplit;

        return (
          <div
            key={`${shift.user_id}-${shift.start_time}-${index}`}
            className="position-absolute"
            style={{
              left: links + 'px',
              top: (5 + bahn * BAHN_HOEHE) + 'px',
              width: breite + 'px',
              height: `${BAHN_HOEHE - 5}px`,
              backgroundColor: isSelected ? '#2196f3' : '#e3f2fd',
              border: isSelected ? '2px solid #1976d2' : '1px solid #90caf9',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: isSelected ? 'bold' : 'normal',
              color: isSelected ? 'white' : '#1976d2',
              zIndex: isSelected ? 10 : 5,
              boxShadow: isSelected ? '0 2px 4px rgba(33, 150, 243, 0.3)' : 'none',
              // Visuelle Kennzeichnung für geteilte Schichten
              borderStyle: isSplit ? 'dashed' : 'solid',
              borderWidth: isSplit ? '3px' : (isSelected ? '2px' : '1px')
            }}
            onClick={() => toggleCashierSelection(shift)}
            title={`${shift.employee_name}\n${isSelected ? `KASSE: ${displayStartTime} - ${displayEndTime}` : `Verfügbar: ${shift.start_time} - ${shift.end_time}`}${isSplit ? '\n(Geteilte Schicht)' : ''}\n${isSelected ? 'Klick zum Deaktivieren' : 'Klick zum als Kassenkraft auswählen'}`}
          >
            <div className="text-center">
              <div style={{ fontSize: '10px', lineHeight: '1' }}>
                {shift.employee_name}
              </div>
              <div style={{ fontSize: '9px', lineHeight: '1', marginTop: '1px' }}>
                {isSelected ? `${displayStartTime}-${displayEndTime}` : `${shift.start_time}-${shift.end_time}`}
              </div>
              {isSelected && (
                <div style={{ fontSize: '8px', lineHeight: '1', marginTop: '1px' }}>
                  {isSplit ? '✂️ KASSE' : '✓ KASSE'}
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      {/* Hinweistext wenn keine Kassenkräfte verfügbar */}
      {cashierShifts.length === 0 && (
        <div className="position-absolute" style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '11px',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          Keine Kassenkräfte an dieser Station eingeteilt
        </div>
      )}
    </div>
  );
}

// Station Timeline Component
function StationTimeline({ 
  station, 
  assignments = [], 
  onDrop, 
  onRemove, 
  onUpdateAssignment, 
  onToggleLockAssignment,
  availabilityData = [],
  flexibleShifts = [],
  selectedDate,
  highlightedEmployeeId = null,
  onHighlight = null,
  getEmployeeColor,
  cashierAssignments = {},
  getAvailableCashiers,
  assignCashier,
  employees = [],
  timeToMinutes
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.EMPLOYEE,
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      const container = document.getElementById(`timeline-${station}`);
      const containerRect = container.getBoundingClientRect();
      // Horizontalen Scroll des Containers mitrechnen, sonst landet die Startzeit falsch.
      const relativeX = offset.x - containerRect.left + (container.scrollLeft || 0);

      // Runde auf 30-Minuten-Raster (48px pro Intervall - kompakt)
      const index = Math.round(relativeX / 48);
      const validIndex = Math.max(0, Math.min(index, timeScale.length - 1));
      const dropTime = timeScale[validIndex];

      // Spätestes Timeline-Ende (rechter Rand, z.B. 01:00) = letzter Slot + 30 Min.
      const maxEndMinutes = timeToMinutes(timeScale[timeScale.length - 1]) + 30;
      const dropMinutes = timeToMinutes(dropTime);

      // Standard 8h-Schicht, begrenzt durch Verfügbarkeit UND das Timeline-Ende –
      // so schießt eine neu abgeworfene Schicht nie über 01:00 / den Container hinaus.
      let endMinutes = dropMinutes + 8 * 60;
      const availability = availabilityData.find(a => a.user_id === item.employee.id);
      if (availability && availability.end_time) {
        endMinutes = Math.min(endMinutes, timeToMinutes(availability.end_time));
      }
      endMinutes = Math.min(endMinutes, maxEndMinutes);
      if (endMinutes - dropMinutes < 30) endMinutes = dropMinutes + 30; // mind. 30 Min
      const endTime = minutesToTime(endMinutes);

      onDrop(item.employee, station, dropTime, endTime);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60) % 24;
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const stationAssignments = (assignments || []).filter(a => 
    a.station_id && station && String(a.station_id).toLowerCase() === station.toLowerCase() ||
    a.station && station && a.station.toLowerCase() === station.toLowerCase()
  ).map(assignment => {
    // Namen korrekt zuordnen - gleiche Logik wie bei Kassenkräften
    const employee = employees.find(emp => emp.id === assignment.user_id);
    
    console.log(`🔍 Name mapping for station assignment ${assignment.user_id}:`, {
      raw_assignment: assignment,
      found_employee: employee,
      assignment_has_firstname: !!assignment.firstname,
      assignment_has_lastname: !!assignment.lastname,
      assignment_has_user_name: !!assignment.user_name,
      assignment_has_employee_name: !!assignment.employee_name
    });
    
    // Priorität: 1. API-Response Namen, 2. Employee Namen, 3. Fallback
    let resolvedName = '';
    if (assignment.user_name) {
      resolvedName = assignment.user_name;
    } else if (assignment.firstname && assignment.lastname) {
      resolvedName = `${assignment.firstname} ${assignment.lastname}`;
    } else if (employee?.name) {
      resolvedName = employee.name;
    } else if (employee?.firstname && employee?.lastname) {
      resolvedName = `${employee.firstname} ${employee.lastname}`;
    } else if (assignment.employee_name) {
      resolvedName = assignment.employee_name;
    } else {
      resolvedName = `User ${assignment.user_id}`;
    }
    
    console.log(`🔍 Resolved station assignment name for user ${assignment.user_id}: "${resolvedName}"`);
    
    return {
      ...assignment,
      employee_name: resolvedName
    };
  });
  console.log(`🏢 Station ${station} assignments:`, stationAssignments);

  // Funktion, um zu prüfen, ob eine Zeit Personalbedarf hat
  const hasStaffingNeed = (time) => {
    const stations = ['G1', 'G5', 'G8', 'G10', 'WG']; // Alle verfügbaren Stationen
    return stations.some(stationId => {
      const relevantShift = flexibleShifts.find(shift => 
        shift.station.toLowerCase() === stationId.toLowerCase() && shift.date === selectedDate
      );
      
      if (!relevantShift) return false;
      
      try {
        let staffingData = null;
        if (relevantShift.hourly_staffing) {
          staffingData = relevantShift.hourly_staffing;
        } else if (relevantShift.staffing_per_hour) {
          staffingData = typeof relevantShift.staffing_per_hour === 'string' 
            ? JSON.parse(relevantShift.staffing_per_hour)
            : relevantShift.staffing_per_hour;
        }
        
        if (staffingData) {
          const hourMatch = time.match(/^(\d{1,2}):(\d{2})$/);
          if (hourMatch) {
            const hour = parseInt(hourMatch[1]);
            const hourKey = hour.toString();
            
            if (staffingData[hourKey] && staffingData[hourKey].needed !== undefined) {
              return Number(staffingData[hourKey].needed) > 0;
            }
          }
        }
      } catch (error) {
        // Fehler ignorieren und false zurückgeben
      }
      
      return false;
    });
  };

  // Optimierter Zeitbereich: nur Zeiten mit Personalbedarf anzeigen.
  // Ab 05:00, damit Frühöffnungs-Bedarf (Wiesn-Garten Sa/Feiertag) planbar ist –
  // die 5/6-Uhr-Spalten erscheinen nur an Tagen mit solchem Bedarf.
  const allPossibleTimes = [];
  for (let hour = 5; hour <= 25; hour++) { // 5:00 bis 1:00 (nächster Tag)
    const displayHour = hour > 24 ? hour - 24 : hour;
    allPossibleTimes.push(`${displayHour.toString().padStart(2, '0')}:00`);
    if (hour < 25) { // Keine halbe Stunde nach 1:00
      allPossibleTimes.push(`${displayHour.toString().padStart(2, '0')}:30`);
    }
  }
  
  // Filtere nur Zeiten mit tatsächlichem Personalbedarf
  const timeScale = allPossibleTimes.filter(time => hasStaffingNeed(time));

  // Exakte Timeline-Breite: 37 Zeitpunkte * 48px = kompakter
  const timelineWidth = timeScale.length * 48;

  const timeToPosition = (time) => {
    const [hours, minutes] = time.split(':').map(Number);

    // 00:xx (Mitternacht) auf die Raster-Schreibweise 24:xx normalisieren, damit eine
    // gespeicherte "00:00" exakt auf der Rasterspalte "24:00" sitzt (sonst 2-Spalten-Versatz).
    const key = (hours === 0)
      ? `24:${minutes.toString().padStart(2, '0')}`
      : `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const index = timeScale.indexOf(key);
    if (index !== -1) {
      // Exakte Position basierend auf timeScale Index (kompakt: 48px pro Slot)
      return index * 48;
    }

    // Nicht im Raster (z.B. herausgefilterte Randzeit wie 01:00): relativ zum LETZTEN
    // sichtbaren Slot anhand echter Minuten extrapolieren. WICHTIG: nicht von 07:00
    // ausgehen – die Skala kann später beginnen (hier 08:00), sonst entsteht Versatz.
    if (!timeScale.length) return 0;
    const absMin = (t) => {
      const [h, m] = t.split(':').map(Number);
      let tot = h * 60 + m;
      if (h >= 0 && h <= 2) tot += 24 * 60; // nach Mitternacht (00:00–02:00) = Folgetag; 05/06 = Frühmorgen
      return tot;
    };
    const lastTime = timeScale[timeScale.length - 1];
    const deltaSlots = (absMin(time) - absMin(lastTime)) / 30;
    return Math.max(0, (timeScale.length - 1 + deltaSlots) * 48);
  };

  const durationToWidth = (startTime, endTime) => {
    const startPos = timeToPosition(startTime);
    const endPos = timeToPosition(endTime);
    return Math.max(endPos - startPos, 48); // Mindestbreite 48px (kompakt)
  };

  // Funktion zur Berechnung der Arbeitszeit
  const calculateWorkingHours = (startTime, endTime) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    const diffMinutes = endMinutes - startMinutes;
    
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}min`;
    }
  };

  // Group overlapping assignments into levels - each employee gets their own level unless times don't overlap at all
  const assignmentLevels = [];
  stationAssignments.forEach(assignment => {
    const startPos = timeToPosition(assignment.start_time);
    const endPos = timeToPosition(assignment.end_time);
    
    console.log(`Processing assignment for employee ${assignment.user_id}: ${assignment.start_time} - ${assignment.end_time} (pos: ${startPos} - ${endPos})`);
    
    let level = 0;
    while (level < assignmentLevels.length) {
      const hasOverlap = assignmentLevels[level].some(existing => {
        const existingStart = timeToPosition(existing.start_time);
        const existingEnd = timeToPosition(existing.end_time);
        
        // Prüfe auf echte zeitliche Überlappung - nur wenn Zeiten sich NICHT überlappen, können sie im gleichen Level sein
        // Füge einen Mindestabstand von 30px (entspricht etwa 30 Minuten) hinzu
        const noOverlap = (endPos <= existingStart - 30) || (startPos >= existingEnd + 30);
        
        console.log(`  Checking against existing employee ${existing.user_id}: ${existing.start_time} - ${existing.end_time} (pos: ${existingStart} - ${existingEnd}), noOverlap: ${noOverlap}`);
        
        return !noOverlap; // Es gibt eine Überlappung, wenn es KEINE klare Trennung gibt
      });
      
      console.log(`  Level ${level}: hasOverlap = ${hasOverlap}`);
      
      if (!hasOverlap) break;
      level++;
    }
    
    console.log(`  Final level for employee ${assignment.user_id}: ${level}`);
    
    if (!assignmentLevels[level]) {
      assignmentLevels[level] = [];
    }
    assignmentLevels[level].push(assignment);
  });

  // Erweiterte Farbpalette für bessere Unterscheidung
  const baseColors = [
    { backgroundColor: '#007bff', color: 'white' }, // Blau
    { backgroundColor: '#17a2b8', color: 'white' }, // Cyan
    { backgroundColor: '#ffc107', color: 'black' }, // Gelb
    { backgroundColor: '#dc3545', color: 'white' }, // Rot
    { backgroundColor: '#6f42c1', color: 'white' }, // Lila
    { backgroundColor: '#fd7e14', color: 'white' }, // Orange
    { backgroundColor: '#e83e8c', color: 'white' }, // Pink
    { backgroundColor: '#20c997', color: 'white' }, // Teal
    { backgroundColor: '#6610f2', color: 'white' }, // Indigo
    { backgroundColor: '#198754', color: 'white' }, // Grün
    { backgroundColor: '#0dcaf0', color: 'black' }, // Light Blue
    { backgroundColor: '#f8f9fa', color: 'black', border: '2px solid #6c757d' }, // Light Gray
  ];

  // Kassenkraft-Farben (grüne Töne)
  const cashierColors = [
    { backgroundColor: '#28a745', color: 'white' }, // Standard Grün
    { backgroundColor: '#20c997', color: 'white' }, // Teal Grün
    { backgroundColor: '#198754', color: 'white' }, // Dunkel Grün
    { backgroundColor: '#25a244', color: 'white' }, // Grün Variante
    { backgroundColor: '#17a673', color: 'white' }, // Teal Variante
    { backgroundColor: '#155724', color: 'white' }, // Sehr Dunkel Grün
  ];

  // Farbpalette für konsistente Farbzuordnung
  const colorPalette = [
    { backgroundColor: '#e74c3c', color: 'white' }, // Rot
    { backgroundColor: '#3498db', color: 'white' }, // Blau
    { backgroundColor: '#2ecc71', color: 'white' }, // Grün
    { backgroundColor: '#f39c12', color: 'white' }, // Orange
    { backgroundColor: '#9b59b6', color: 'white' }, // Lila
    { backgroundColor: '#1abc9c', color: 'white' }, // Türkis
    { backgroundColor: '#e67e22', color: 'white' }, // Dunkelorange
    { backgroundColor: '#34495e', color: 'white' }, // Dunkelgrau
    { backgroundColor: '#e91e63', color: 'white' }, // Pink
    { backgroundColor: '#8bc34a', color: 'white' }, // Hellgrün
    { backgroundColor: '#ff9800', color: 'white' }, // Amber
    { backgroundColor: '#795548', color: 'white' }, // Braun
    { backgroundColor: '#607d8b', color: 'white' }, // Blaugrau
    { backgroundColor: '#673ab7', color: 'white' }, // Dunkellila
    { backgroundColor: '#009688', color: 'white' }, // Teal
    { backgroundColor: '#ff5722', color: 'white' }, // Tiefrot
    { backgroundColor: '#4caf50', color: 'white' }, // Materialgrün
    { backgroundColor: '#2196f3', color: 'white' }, // Materialblau
    { backgroundColor: '#ff9900', color: 'white' }, // Dunklesorange
    { backgroundColor: '#00bcd4', color: 'white' }, // Cyan
  ];

  // Berechne Timeline-Höhe basierend auf Anzahl der Assignment-Level
  const calculateTimelineHeight = () => {
    if (assignmentLevels.length === 0) {
      return 60; // Mindesthöhe für leere Timeline
    }
    
    // Berechne die benötigte Höhe basierend auf der Anzahl der Level
    const levelHeight = 22; // Höhe pro Level (18px für Balken + 4px Abstand)
    const totalHeight = assignmentLevels.length * levelHeight + 20; // +20px für Padding
    
    console.log(`Timeline height calculation: ${assignmentLevels.length} levels × ${levelHeight}px + 20px = ${totalHeight}px`);
    
    return Math.max(totalHeight, 60); // Mindestens 60px Höhe
  };

  // Funktion zur Berechnung der Personalanforderungen für jeden Zeitpunkt
  const getStaffingForTime = (time) => {
    // Finde die entsprechende flexible Schicht für diese Station und dieses Datum
    // Note: Backend uses lowercase station IDs, but frontend uses uppercase
    const relevantShift = flexibleShifts.find(shift => 
      shift.station.toLowerCase() === station.toLowerCase() && shift.date === selectedDate
    );
    
    let required = 0; // Fallback - kein Bedarf wenn keine Daten vorhanden
    
    if (relevantShift) {
      try {
        let staffingData = null;
        
        // Try different possible data formats
        if (relevantShift.hourly_staffing) {
          // New format from backend
          staffingData = relevantShift.hourly_staffing;
        } else if (relevantShift.staffing_per_hour) {
          // Old format - try to parse JSON
          staffingData = typeof relevantShift.staffing_per_hour === 'string' 
            ? JSON.parse(relevantShift.staffing_per_hour)
            : relevantShift.staffing_per_hour;
        }
        
        if (staffingData) {
          // Convert time format from "HH:MM" to hour number
          // e.g., "08:00" -> "8", "07:30" -> "7" (round down for half hours)
          const hourMatch = time.match(/^(\d{1,2}):(\d{2})$/);
          if (hourMatch) {
            const hour = parseInt(hourMatch[1]);
            const hourKey = hour.toString();
            
            // Look for exact hour match
            if (staffingData[hourKey] && staffingData[hourKey].needed !== undefined) {
              required = Number(staffingData[hourKey].needed); // 0 ist ein gültiger Wert!
            } else {
              required = 0; // Wenn keine Daten da sind, kein Bedarf
            }
          }
        }
      } catch (error) {
        console.warn('Fehler beim Parsen der Schichtplanung für Station', station, ':', error);
        required = 0;
      }
    }
    
    // Zähle aktuelle Mitarbeiter zu diesem Zeitpunkt
    const current = stationAssignments.filter(assignment => {
      const startMinutes = timeToMinutes(assignment.start_time);
      const endMinutes = timeToMinutes(assignment.end_time);
      const timeMinutes = timeToMinutes(time);
      
      return timeMinutes >= startMinutes && timeMinutes < endMinutes;
    }).length;
    
    return { current, required };
  };

  // Funktion zur Bestimmung der Farbe basierend auf Erfüllungsgrad
  const getStaffingColor = (current, required) => {
    if (current > required) {
      return '#8B5CF6'; // Lila - Überbesetzung
    }
    
    const fulfillment = current / required;
    
    if (fulfillment >= 1) {
      return '#10B981'; // Grün - Vollständig erfüllt
    } else if (fulfillment >= 0.8) {
      return '#84CC16'; // Hellgrün - Fast erfüllt
    } else if (fulfillment >= 0.6) {
      return '#EAB308'; // Gelb - Teilweise erfüllt
    } else if (fulfillment >= 0.4) {
      return '#F97316'; // Orange - Schlecht erfüllt
    } else {
      return '#EF4444'; // Rot - Sehr schlecht erfüllt
    }
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '4px' }}>
        <h6 className="mb-0">{getStationName(station)}</h6>
        <span className="badge bg-info">
          {stationAssignments.length} Mitarbeiter
        </span>
      </div>
      
      {/* Anzeige wenn keine Schichtplanung vorhanden */}
      {timeScale.length === 0 ? (
        <div className="alert alert-info">
          <small>
            <i className="bi bi-info-circle me-1"></i>
            Keine Schichtplanung für diesen Tag vorhanden. Bitte erstellen Sie zuerst eine Schichtvorlage.
          </small>
        </div>
      ) : (
      <>
      {/* Time Grid Header */}
      <div className="position-relative" style={{ height: '50px', width: `${timelineWidth}px`, marginBottom: '0px' }}>
        <div className="d-flex">
          {timeScale.map((time, index) => {
            const staffing = getStaffingForTime(time);
            const staffingColor = getStaffingColor(staffing.current, staffing.required);
            
            return (
              <div 
                key={time} 
                className={`text-center ${time.endsWith(':00') ? 'fw-bold' : ''}`}
                style={{ 
                  width: '48px',
                  borderLeft: time.endsWith(':00') ? '2px solid #dee2e6' : '1px solid #f8f9fa',
                  paddingLeft: '2px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}
              >
                {/* Zeit */}
                <div 
                  style={{ 
                    fontSize: time.endsWith(':00') ? '12px' : '10px',
                    color: time.endsWith(':00') ? '#495057' : '#6c757d',
                    lineHeight: '1',
                    marginBottom: '2px'
                  }}
                >
                  {time}
                </div>
                
                {/* Personalanforderung */}
                <div 
                  style={{ 
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: staffingColor,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    borderRadius: '3px',
                    padding: '1px 3px',
                    margin: '0 2px',
                    border: `1px solid ${staffingColor}`,
                    lineHeight: '1'
                  }}
                  title={`Aktuell: ${staffing.current}, Benötigt: ${staffing.required}`}
                >
                  {staffing.current}/{staffing.required}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Kassenkraft-Zuweisungsleiste */}
      <CashierBar
        station={station}
        timeScale={timeScale}
        cashierAssignments={cashierAssignments}
        getAvailableCashiers={getAvailableCashiers}
        assignCashier={assignCashier}
        employees={employees}
        assignments={assignments}
        availabilityData={availabilityData}
        timeToPosition={timeToPosition}
        durationToWidth={durationToWidth}
        timeToMinutes={timeToMinutes}
      />
      
      {/* Drop Zone with Timeline */}
      <div
        ref={drop}
        id={`timeline-${station}`}
        className={`position-relative timeline-container ${isOver ? 'bg-light' : 'bg-white'}`}
        style={{ 
          height: calculateTimelineHeight() + 'px', // Dynamische Höhenberechnung
          width: `${timelineWidth}px`,
          overflowX: 'auto',
          marginTop: '-5px',
          border: 'none',
          borderRadius: '0px',
          padding: '0px'
        }}
      >
        {/* Time Grid Lines */}
        {timeScale.map((time, index) => (
          <div
            key={time}
            className="position-absolute border-start"
            style={{
              left: `${index * 48}px`,
              top: '0',
              height: '100%',
              borderColor: time.endsWith(':00') ? '#dee2e6' : '#f8f9fa',
              borderWidth: time.endsWith(':00') ? '2px' : '1px'
            }}
          />
        ))}
        
        {/* Employee Bars */}
        {assignmentLevels.map((level, levelIndex) =>
          level.map((assignment, assignmentIndex) => {
            const availability = availabilityData.find(a => a.user_id === assignment.user_id);
            const isCashier = availability?.can_work_cash_register === 1;
            const employeeColorStyle = getEmployeeColor(assignment.user_id, isCashier);
            
            return (
              <div
                key={`${levelIndex}-${assignmentIndex}`}
                style={{
                  position: 'absolute',
                  top: `${levelIndex * 22}px`, // Erhöhter Abstand: 22px für bessere Sichtbarkeit
                  zIndex: 10
                }}
              >
                <ResizableEmployeeBar
                  assignment={assignment}
                  availabilityData={availabilityData}
                  onUpdate={onUpdateAssignment}
                  onRemove={onRemove}
                  onToggleLock={onToggleLockAssignment}
                  timeToPosition={timeToPosition}
                  durationToWidth={durationToWidth}
                  employeeColorStyle={employeeColorStyle}
                  isCashier={isCashier}
                  calculateWorkingHours={calculateWorkingHours}
                  timeScale={timeScale}
                  highlightedEmployeeId={highlightedEmployeeId}
                  onHighlight={onHighlight}
                />
              </div>
            );
          })
        )}
        
        {/* Empty State */}
        {stationAssignments.length === 0 && (
          <div className="text-center text-muted mt-4">
            <i className="bi bi-arrow-down fs-4"></i>
            <div>Mitarbeiter hier ablegen</div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

// Main Component
function PersonalPlanningEnhanced({ user }) {
  console.log('🎯 PersonalPlanningEnhanced component loaded!', { user });
  
  const [selectedDate, setSelectedDate] = useState(() => {
    const saved = localStorage.getItem('lastSelectedDate');
    const defaultDate = saved || new Date().toISOString().split('T')[0];
    console.log('📅 Initial selectedDate set to:', defaultDate);
    return defaultDate;
  });
  const [employees, setEmployees] = useState([]);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [stations, setStations] = useState([]);
  const [flexibleShifts, setFlexibleShifts] = useState([]);
  const [partnerPreferences, setPartnerPreferences] = useState([]);
  const [cashierAssignments, setCashierAssignments] = useState({}); // Station-spezifische Kassenkraft-Zuweisungen: {station: {timeSlot: assignment}}
  const [stationStats, setStationStats] = useState([]); // Station-Statistiken für Mitarbeiter
  const [prevAssignmentsByUser, setPrevAssignmentsByUser] = useState({}); // Vortags-Schichten je Mitarbeiter (Ruhezeit-Blick)
  const [fairnessByUser, setFairnessByUser] = useState({}); // Saison-Fairness: verfügbare vs. verplante Tage/Stunden
  const [sortByFairness, setSortByFairness] = useState(false); // Verfügbaren-Liste: Benachteiligte zuerst
  // Schichtbörse: Meldungen auf freie Schichten (nur für den gewählten Tag angezeigt)
  const [shiftClaims, setShiftClaims] = useState([]);
  const [declineBox, setDeclineBox] = useState(null); // Absage mit Begruendung
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [suggestions] = useState([]);
  
  // Search functionality
  const [availableSearch, setAvailableSearch] = useState('');
  const [plannedSearch, setPlannedSearch] = useState('');
  
  // Neue State-Variablen für Highlighting und Filter
  const [highlightedEmployeeId, setHighlightedEmployeeId] = useState(null);

  // Farbzuordnung basierend auf Partnerschaften
  const getEmployeeColor = (employeeId, isCashier) => {
    // Fallback für undefined employeeId
    if (!employeeId) {
      return { backgroundColor: '#6c757d', color: 'white' }; // Grau als Fallback
    }

    // Farbpalette für konsistente Farbzuordnung (erweitert für mehr Einzigartigkeit)
    const colorPalette = [
      { backgroundColor: '#e74c3c', color: 'white' }, // Rot
      { backgroundColor: '#3498db', color: 'white' }, // Blau
      { backgroundColor: '#2ecc71', color: 'white' }, // Grün
      { backgroundColor: '#f39c12', color: 'white' }, // Orange
      { backgroundColor: '#9b59b6', color: 'white' }, // Lila
      { backgroundColor: '#1abc9c', color: 'white' }, // Türkis
      { backgroundColor: '#e67e22', color: 'white' }, // Dunkelorange
      { backgroundColor: '#34495e', color: 'white' }, // Dunkelgrau
      { backgroundColor: '#e91e63', color: 'white' }, // Pink
      { backgroundColor: '#8bc34a', color: 'white' }, // Hellgrün
      { backgroundColor: '#ff9800', color: 'white' }, // Amber
      { backgroundColor: '#795548', color: 'white' }, // Braun
      { backgroundColor: '#607d8b', color: 'white' }, // Blaugrau
      { backgroundColor: '#673ab7', color: 'white' }, // Dunkellila
      { backgroundColor: '#009688', color: 'white' }, // Teal
      { backgroundColor: '#ff5722', color: 'white' }, // Tiefrot
      { backgroundColor: '#4caf50', color: 'white' }, // Materialgrün
      { backgroundColor: '#2196f3', color: 'white' }, // Materialblau
      { backgroundColor: '#ff9900', color: 'white' }, // Dunklesorange
      { backgroundColor: '#00bcd4', color: 'white' }, // Cyan
      { backgroundColor: '#8e24aa', color: 'white' }, // Violett
      { backgroundColor: '#d32f2f', color: 'white' }, // Dunkelrot
      { backgroundColor: '#388e3c', color: 'white' }, // Dunkelgrün
      { backgroundColor: '#1976d2', color: 'white' }, // Dunkelblau
      { backgroundColor: '#f57c00', color: 'white' }, // Dunkler Amber
      { backgroundColor: '#5d4037', color: 'white' }, // Dunkler Braun
      { backgroundColor: '#455a64', color: 'white' }, // Slate
      { backgroundColor: '#e65100', color: 'white' }, // Dunkler Orange
      { backgroundColor: '#bf360c', color: 'white' }, // Burnt Orange
      { backgroundColor: '#2e7d32', color: 'white' }, // Forest Green
    ];

    // Hilfsfunktion: Bevorzugte Station eines Mitarbeiters finden
    const getPreferredStation = (userId) => {
      const userStats = stationStats.find(stat => stat.user_id === userId);
      return userStats?.preferred_station || null;
    };

    // Finde alle Partner für diesen Mitarbeiter
    const findPartnerGroup = (userId) => {
      const visited = new Set();
      const partnerGroup = new Set();
      
      const findConnectedPartners = (currentUserId) => {
        if (visited.has(currentUserId)) return;
        visited.add(currentUserId);
        partnerGroup.add(currentUserId);
        
        // Finde alle direkten Partner-Verbindungen
        const connections = partnerPreferences.filter(pref => 
          pref.user_id === currentUserId || pref.preferred_partner_id === currentUserId
        );
        
        connections.forEach(pref => {
          const partnerId = pref.user_id === currentUserId ? pref.preferred_partner_id : pref.user_id;
          if (!visited.has(partnerId)) {
            findConnectedPartners(partnerId);
          }
        });
      };
      
      findConnectedPartners(userId);
      return Array.from(partnerGroup).sort((a, b) => a - b);
    };

    const partnerGroup = findPartnerGroup(employeeId);
    
    // Wenn der Mitarbeiter Partner hat, verwende die niedrigste ID als Basis
    // Andernfalls verwende die eigene ID
    let baseId;
    if (partnerGroup.length > 1) {
      // Hat Partner - verwende niedrigste ID der Gruppe
      baseId = Math.min(...partnerGroup);
    } else {
      // Keine Partner - verwende eigene ID für einzigartige Farbe
      baseId = employeeId;
    }
    
    // Stelle sicher, dass jede ID eine einzigartige Farbe bekommt
    // durch Verwendung einer deterministischen Hash-Funktion
    const generateColorIndex = (id) => {
      // Einfacher Hash um bessere Verteilung zu gewährleisten
      let hash = 0;
      const str = id.toString();
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // 32-bit integer
      }
      return Math.abs(hash) % colorPalette.length;
    };
    
    const colorIndex = generateColorIndex(baseId);
    
    return colorPalette[colorIndex];
  };
  const [showOnlyCashiers, setShowOnlyCashiers] = useState(false);

  // Partner-Gruppierung erstellen

  const createPartnerGroups = useCallback((employees) => {
    const processedEmployees = new Set();
    const groups = [];
    const singleEmployees = [];

    employees.forEach(emp => {
      if (processedEmployees.has(emp.id)) return;

      // Finde Partner für diesen Mitarbeiter
      const employeePartners = partnerPreferences.filter(pref => 
        pref.user_id === emp.id || pref.preferred_partner_id === emp.id
      );

      if (employeePartners.length === 0) {
        // Kein Partner - einzelner Mitarbeiter
        singleEmployees.push(emp);
        processedEmployees.add(emp.id);
        return;
      }

      // Alle Partner-IDs sammeln (bidirektional)
      const partnerIds = new Set();
      employeePartners.forEach(pref => {
        if (pref.user_id === emp.id) {
          partnerIds.add(pref.preferred_partner_id);
        } else {
          partnerIds.add(pref.user_id);
        }
      });
      partnerIds.add(emp.id); // Sich selbst hinzufügen

      // Alle verfügbaren Partner für diesen Tag finden
      const groupMembers = employees.filter(e => partnerIds.has(e.id));
      
      if (groupMembers.length > 1) {
        // Partner-Gruppe erstellen
        groups.push({
          id: `group-${emp.id}`,
          type: 'group',
          members: groupMembers,
          name: groupMembers.map(m => m.name ? m.name.split(' ')[0] : (m.firstname || m.name?.split(' ')[0] || 'User')).join(' & '),
          canWorkCash: groupMembers.some(m => {
            const availability = availabilityData.find(avail => avail.user_id === m.id);
            return availability && availability.can_work_cash_register === 1;
          })
        });
        
        // Alle Mitglieder als verarbeitet markieren
        groupMembers.forEach(member => processedEmployees.add(member.id));
      } else {
        // Partner nicht verfügbar - einzelner Mitarbeiter
        singleEmployees.push(emp);
        processedEmployees.add(emp.id);
      }
    });

    return { groups, singleEmployees };
  }, [partnerPreferences, availabilityData]);

  // Berechne verfügbare Mitarbeiter und displayItems
  const displayItems = useMemo(() => {
    console.log('🔄 Recalculating displayItems with', employees.length, 'employees and', availabilityData.length, 'availability records');
    
    const availableEmployees = employees.filter(emp => {
      const availability = availabilityData.find(avail => avail.user_id === emp.id);
      const isAvailable = availability && availability.is_available === 1;
      const canWorkCash = availability && availability.can_work_cash_register === 1;
      const matchesCashierFilter = !showOnlyCashiers || canWorkCash;
      
      console.log('🔍 Filter check for', emp.name, ':', {
        isAvailable,
        canWorkCash, 
        showOnlyCashiers,
        matchesCashierFilter,
        finalResult: isAvailable && matchesCashierFilter
      });
      
      return isAvailable && matchesCashierFilter;
    });

    console.log('✅ Available employees after filtering:', availableEmployees.length);

    // Partner-Gruppen und einzelne Mitarbeiter erstellen
    const { groups: partnerGroups, singleEmployees } = createPartnerGroups(availableEmployees);
    
    // Kombiniere Gruppen und einzelne Mitarbeiter für die Anzeige
    const items = [
      ...partnerGroups,
      ...singleEmployees.map(emp => ({
        ...emp,
        type: 'single',
        canWorkCash: (() => {
          const availability = availabilityData.find(avail => avail.user_id === emp.id);
          return availability && availability.can_work_cash_register === 1;
        })()
      }))
    ];

    // Fairness-Sortierung: die am stärksten Benachteiligten (Rang 1, 2, …) zuerst.
    // Partner-Gruppen zählen mit dem besten (niedrigsten) Rang ihrer Mitglieder.
    if (sortByFairness) {
      const rankOf = (id) => (fairnessByUser[id] && fairnessByUser[id].rank) || Infinity;
      const itemRank = (item) => item.type === 'group'
        ? Math.min(...item.members.map((m) => rankOf(m.id)))
        : rankOf(item.id);
      items.sort((a, b) => itemRank(a) - itemRank(b));
    }

    console.log('🎯 Final displayItems:', items.length, 'items (', partnerGroups.length, 'groups,', singleEmployees.length, 'singles)');
    return items;
  }, [employees, availabilityData, showOnlyCashiers, partnerPreferences, sortByFairness, fairnessByUser]);

  // Filter functions for search
  const filterBySearch = (items, searchTerm) => {
    if (!searchTerm.trim()) return items;
    
    return items.filter(item => {
      if (item.type === 'group') {
        // Search in group member names
        return item.members.some(member => 
          (member.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (member.firstname?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
          (member.lastname?.toLowerCase() || '').includes(searchTerm.toLowerCase())
        );
      } else {
        // Search in single employee name
        return (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
               (item.firstname?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
               (item.lastname?.toLowerCase() || '').includes(searchTerm.toLowerCase());
      }
    });
  };


  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Zurück zum Tab (nach Pause/anderem Fenster): Einsätze frisch laden – Absagen per WhatsApp ändern den Plan im Hintergrund
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible' && selectedDate) loadAssignments(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log('🚀 PersonalPlanningEnhanced useEffect triggered, selectedDate:', selectedDate);
    if (selectedDate) {
      localStorage.setItem('lastSelectedDate', selectedDate);
      console.log('📅 About to call loadData for date:', selectedDate);
      loadData();
    } else {
      console.log('⚠️ selectedDate is empty, not loading data');
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    console.log('📊 loadData started for selectedDate:', selectedDate);
    setLoading(true);
    try {
      console.log('🔄 About to load: employees, assignments, flexibleShifts, partnerPreferences, stationStats');
      await Promise.all([
        loadEmployees(),
        loadAssignments(),
        loadFlexibleShifts(),
        loadPartnerPreferences(),
        loadStationStats(),
        loadShiftClaims()
      ]);
      console.log('✅ All data loaded successfully');
    } catch (error) {
      console.error('❌ Error in loadData:', error);
      showToast('Fehler beim Laden der Daten', 'danger');
    } finally {
      setLoading(false);
      console.log('🏁 loadData completed');
    }
  };

  const loadEmployees = async () => {
    try {
      console.log('🔄 Loading employees for date:', selectedDate);
      // Lade verfügbare Mitarbeiter für das gewählte Datum
      const availabilityData = await apiCall(`/user-availability/${selectedDate}`);
      console.log('📥 Raw availability data:', availabilityData);
      
      // Lade alle Benutzer, um can_work_cash_register-Information zu bekommen
      const usersData = await apiCall(`/users`);
      console.log('📥 Raw users data count:', usersData?.length);
      
      // Kombiniere Daten
      const availableEmployees = Array.isArray(availabilityData) ? availabilityData.filter(emp => emp.is_available === 1) : [];
      console.log('📊 Available employees count:', availableEmployees.length);
      
      // Erstelle vollständigen Datensatz mit can_work_cash_register Information
      const enhancedAvailabilityData = Array.isArray(availabilityData) 
        ? availabilityData.map(avail => {
            const userInfo = usersData.find(u => u.id === avail.user_id);
            return {
              ...avail,
              can_work_cash_register: userInfo?.can_work_cash_register || 0,
              name: userInfo?.name,
              firstname: userInfo?.firstname,
              lastname: userInfo?.lastname,
              role: userInfo?.role
            };
          })
        : [];
      
      console.log('📊 Enhanced availability data sample:', enhancedAvailabilityData.slice(0, 3));
      
      // Erstelle Mitarbeiterliste
      const transformedEmployees = availableEmployees.map(emp => {
        const userInfo = usersData.find(u => u.id === emp.user_id);
        return {
          id: emp.user_id,
          name: userInfo?.name || 'Unbekannter Benutzer',
          firstname: userInfo?.firstname,
          lastname: userInfo?.lastname,
          can_work_cash_register: userInfo?.can_work_cash_register || 0
        };
      });
      
      console.log('📊 Transformed employees sample:', transformedEmployees.slice(0, 3));
      console.log('✅ Setting employees and availability data in state');
      setEmployees(transformedEmployees);
      setAvailabilityData(enhancedAvailabilityData);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
      setEmployees([]);
      setAvailabilityData([]);
    }
  };

  // Meldungen auf freie Schichten laden (Schichtbörse)
  const loadShiftClaims = async () => {
    try {
      const d = await apiCall('/admin/shift-claims');
      setShiftClaims(Array.isArray(d) ? d : []);
    } catch (_) { setShiftClaims([]); }
  };
  const claimsToday = shiftClaims.filter((o) => o.date === selectedDate);

  // ✓ = einplanen (legt die Schicht an und schickt die Änderungs-Nachricht) · ✗ = ablehnen
  const decideClaim = async (claim, offer, accept, note = '', notifyEmp = false) => {
    try {
      if (accept) {
        const day = await apiCall(`/planning/assignments/${offer.date}`).catch(() => []);
        const from = offer.from * 60, to = (offer.to === 25 ? 25 : offer.to) * 60;
        const toMin = (t) => { const [h, m] = String(t).split(':').map(Number); return h * 60 + (m || 0); };
        const schonDa = (Array.isArray(day) ? day : []).some((a) => {
          if (Number(a.user_id) !== Number(claim.user_id)) return false;
          let s = toMin(a.start_time); let e = toMin(a.end_time); if (e <= s) e += 1440;
          return Math.min(e, to) - Math.max(s, from) > 0;   // ueberschneidet sich -> schon eingeplant
        });
        if (!schonDa) {
          const res = await apiCall('/planning/assign', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date: offer.date,
              user_id: claim.user_id,
              station_id: String(offer.station).toUpperCase() === 'WIESN-GARTEN' ? 'WG' : String(offer.station).toUpperCase(),
              start_time: `${String(offer.from).padStart(2, '0')}:00`,
              end_time: `${String(offer.to === 25 ? 1 : offer.to).padStart(2, '0')}:00`,
              is_cashier: false,
            }),
          });
          if (!res || !res.success) { showToast('Einplanen fehlgeschlagen', 'danger'); return; }
        }
      }
      const r = await apiCall(`/admin/shift-claims/${claim.claim_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision: accept ? 'assigned' : 'declined', note, notify: !accept && notifyEmp }),
      });
      setDeclineBox(null);
      await Promise.all([loadAssignments(), loadShiftClaims()]);
      if (!accept) showToast(
        !notifyEmp ? `${claim.name} abgelehnt`
          : r && r.notified ? `${claim.name} abgelehnt – WhatsApp verschickt`
          : `${claim.name} abgelehnt – ⚠️ WhatsApp NICHT verschickt (${(r && r.reason) || 'unbekannt'})`,
        notifyEmp && !(r && r.notified) ? 'warning' : 'secondary');
      else if (r && r.notified) showToast(`${claim.name} eingeplant – WhatsApp verschickt (${r.channel === 'template' ? 'Template' : 'Chat'})`, 'success');
      else showToast(`${claim.name} eingeplant – ⚠️ WhatsApp NICHT verschickt (${(r && r.reason) || 'unbekannt'})`, 'warning');
    } catch (e) {
      showToast('Aktion fehlgeschlagen', 'danger');
    }
  };

  const loadAssignments = async () => {
    try {
      console.log('🔄 Loading assignments for date:', selectedDate);
      const data = await apiCall(`/planning/assignments/${selectedDate}`);
      setAssignments(Array.isArray(data) ? data : []);

      // Vortags-Schichten laden – die Mitarbeiter-Karten zeigen sie an, damit
      // niemand bis 01:00 und am Folgetag wieder ab 07:00 eingeplant wird (Ruhezeit)
      try {
        const d = new Date(selectedDate + 'T12:00:00');
        d.setDate(d.getDate() - 1);
        const prevDate = d.toISOString().slice(0, 10);
        const prevData = await apiCall(`/planning/assignments/${prevDate}`);
        const map = {};
        (Array.isArray(prevData) ? prevData : []).forEach((a) => {
          (map[a.user_id] = map[a.user_id] || []).push(a);
        });
        setPrevAssignmentsByUser(map);
      } catch (e) {
        setPrevAssignmentsByUser({});
      }

      // Lade auch die Kassenkraft-Zuweisungen
      await loadCashierAssignments();

      // Fairness-Kennzahlen (Saison): aktualisieren sich mit jeder Zuweisung.
      // Rang 1 = am stärksten benachteiligt (niedrigste Verplan-Quote bei
      // vorhandener Verfügbarkeit) – „sollte als Nächstes eingeteilt werden".
      try {
        const fairness = await apiCall('/planning/fairness');
        const ranked = (Array.isArray(fairness) ? fairness : [])
          .filter((f) => f.avail_days > 0)
          .map((f) => ({ ...f, ratio: f.planned_days / f.avail_days, pct: Math.round((f.planned_days / f.avail_days) * 100) }));
        ranked.sort((a, b) => (a.ratio - b.ratio) || (b.avail_days - a.avail_days));
        const fMap = {};
        ranked.forEach((f, i) => { fMap[f.user_id] = { ...f, rank: i + 1, total: ranked.length }; });
        setFairnessByUser(fMap);
      } catch (e) {
        // Fairness ist nice-to-have – Fehler still ignorieren
      }
    } catch (error) {
      console.error('Fehler beim Laden der Zuweisungen:', error);
      setAssignments([]);
    }
  };

  // Lade Kassenkraft-Zuweisungen aus der Datenbank
  const loadCashierAssignments = async () => {
    try {
      console.log('🔄 Loading cashier assignments for date:', selectedDate);
      const dailyShifts = await apiCall(`/daily-shifts?date=${selectedDate}`);
      
      if (!dailyShifts || !Array.isArray(dailyShifts)) {
        console.error('❌ Failed to load cashier assignments - invalid data');
        return;
      }
      console.log('📥 Received daily shifts for cashier loading:', dailyShifts);

      // Erstelle cashierAssignments aus den daily shifts mit is_cashier = true.
      //
      // WICHTIG: Station und Schlüssel müssen exakt so gebildet werden wie beim
      // Lesen in der Kassenkraft-Leiste (cashierKey / stationKey). Vorher stand
      // hier nur die Startzeit als Schlüssel ("14:00") und die Leiste suchte
      // "241-14:00" – der Treffer ging also immer daneben und die Auswahl sah
      // nach jedem Neuladen leer aus, obwohl sie in der DB stand.
      const newCashierAssignments = {};

      dailyShifts.forEach(shift => {
        if (shift.is_cashier && shift.station_id && shift.start_time) {
          const station = stationKey(shift.station_id);
          const timeSlot = cashierKey(shift.user_id, shift.start_time);

          if (!newCashierAssignments[station]) {
            newCashierAssignments[station] = {};
          }

          // Gespeichertes Kassen-Zeitfenster gewinnt (Übergabe an die nächste
          // Kraft); ohne Fenster führt die Person die Kasse die ganze Schicht.
          const kasseVon = shift.cashier_start || shift.start_time;
          const kasseBis = shift.cashier_end || shift.end_time;
          newCashierAssignments[station][timeSlot] = {
            assignment_id: shift.id,
            user_id: shift.user_id,
            employee_name: shift.employee_name || `User ${shift.user_id}`,
            start_time: kasseVon,
            end_time: kasseBis,
            shift_start: shift.start_time,
            shift_end: shift.end_time,
            isSplit: kasseVon !== shift.start_time || kasseBis !== shift.end_time,
            station_id: shift.station_id
          };

          console.log(`💰 Loaded cashier assignment: Station ${station}, Key ${timeSlot}, Kasse ${kasseVon}-${kasseBis}, Employee: ${shift.employee_name || shift.user_id}`);
        }
      });

      console.log('📊 Loaded cashier assignments:', newCashierAssignments);
      setCashierAssignments(newCashierAssignments);
      
    } catch (error) {
      console.error('❌ Error loading cashier assignments:', error);
    }
  };

  // Hilfsfunktion für Zeitkonvertierung (für alle Funktionen verfügbar)
  const timeToMinutes = useCallback((time) => {
    const [hours, minutes] = time.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;
    if (hours >= 0 && hours <= 2) totalMinutes += 24 * 60; // Nächster Tag (00:00–02:00); 05/06 Uhr = Frühmorgen
    return totalMinutes;
  }, []);

  // Hilfsfunktion um Minuten zurück in Zeit umzuwandeln
  const minutesToTime = useCallback((minutes) => {
    const hours = Math.floor(minutes / 60) % 24;
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }, []);

  // Funktion zum Finden verfügbarer Kassenkräfte für eine bestimmte Zeit
  const getAvailableCashiers = useCallback((startTime, endTime) => {
    return assignments.filter(assignment => {
      // Prüfe ob der Mitarbeiter als Kassenkraft arbeitet
      const employee = employees.find(emp => emp.id === assignment.user_id);
      const availability = availabilityData.find(avail => avail.user_id === assignment.user_id);
      
      if (!employee || !availability || availability.can_work_cash_register !== 1) {
        return false;
      }
      
      // Prüfe Zeitüberschneidung
      const assignmentStart = timeToMinutes(assignment.start_time);
      const assignmentEnd = timeToMinutes(assignment.end_time);
      const requestStart = timeToMinutes(startTime);
      const requestEnd = timeToMinutes(endTime);
      
      // Überschneidung wenn Start < anderes Ende UND Ende > anderer Start
      return assignmentStart < requestEnd && assignmentEnd > requestStart;
    }).map(assignment => {
      const employee = employees.find(emp => emp.id === assignment.user_id);
      return {
        ...assignment,
        employee_name: employee.name || `${employee.firstname || ''} ${employee.lastname || ''}`.trim() || `User ${employee.id}`,
        station_id: assignment.station
      };
    });
  }, [assignments, employees, availabilityData, timeToMinutes]);

  // Funktion zum Zuweisen einer Kassenkraft (station-spezifisch)
  const assignCashier = useCallback(async (station, timeSlot, cashierAssignment, entfernenVon) => {
    // Update Frontend State
    const sKey = stationKey(station);
    setCashierAssignments(prev => {
      const newAssignments = { ...prev };

      // Stelle sicher, dass die Station existiert
      if (!newAssignments[sKey]) {
        newAssignments[sKey] = {};
      } else {
        newAssignments[sKey] = { ...newAssignments[sKey] };
      }

      if (cashierAssignment === undefined) {
        // Entferne die Zuweisung für diese Station
        delete newAssignments[sKey][timeSlot];

        // Entferne leere Station-Objekte
        if (Object.keys(newAssignments[sKey]).length === 0) {
          delete newAssignments[sKey];
        }
      } else {
        // Füge die Zuweisung für diese Station hinzu
        newAssignments[sKey][timeSlot] = cashierAssignment;
      }

      return newAssignments;
    });

    // Persistiere die Zuweisung. Es wird immer GENAU die angeklickte Zeile
    // geschrieben – vorher versuchte die Funktion, per Startzeit "alle anderen"
    // zu finden, prüfte dabei ein `.ok` an einem Array (immer falsch) und
    // schrieb ausserdem nur, wenn `assignment_id` gesetzt war. Bei einem frisch
    // angeklickten Balken heisst das Feld aber `id` – deshalb landete die
    // Auswahl nie in der Datenbank.
    const eintrag = cashierAssignment || entfernenVon;
    const id = eintrag && (eintrag.assignment_id || eintrag.id);
    if (!id) {
      console.warn('⚠️ Kassenkraft nicht gespeichert – keine Zuweisungs-ID', { station, timeSlot, cashierAssignment });
      return;
    }
    try {
      const an = !!cashierAssignment;
      // Zeitfenster nur mitschicken, wenn die Kasse NICHT die ganze Schicht
      // läuft (Übergabe an die nächste Kraft). Sonst NULL = ganze Schicht.
      const schichtVon = eintrag.shift_start || eintrag.start_time;
      const schichtBis = eintrag.shift_end || eintrag.end_time;
      const teil = an && (eintrag.start_time !== schichtVon || eintrag.end_time !== schichtBis);
      const result = await apiCall(`/daily-shifts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_cashier: an,
          cashier_start: teil ? eintrag.start_time : null,
          cashier_end: teil ? eintrag.end_time : null,
        }),
      });
      console.log(`${an ? '✅' : '🗑️'} Kassenkraft ${an ? 'gespeichert' : 'entfernt'} (Zuweisung ${id})`, result);
    } catch (error) {
      console.error('❌ Error updating cashier assignment:', error);
    }
  }, [selectedDate]);

  const loadFlexibleShifts = async () => {
    console.log('🔄 loadFlexibleShifts started for date:', selectedDate);
    try {
      console.log('🔗 Fetching flexible shifts for date:', selectedDate);
      
      const data = await apiCall(`/flexible-shifts?date=${selectedDate}`);
      const plans = Array.isArray(data) ? data : [];
      
      console.log('📦 Raw API response:', data);
      console.log('📦 Plans array:', plans);
      console.log('📦 Plans length:', plans.length);
      
      // Transform raw plan data to shifts format expected by PersonalPlanningEnhanced
      const shifts = [];
      const uniqueStations = [];
      
      plans.forEach((plan, planIndex) => {
        console.log(`📋 Processing plan ${planIndex}:`, plan);
        try {
          const planData = JSON.parse(plan.plan_data);
          console.log(`🔍 Parsed plan data for plan ${planIndex}:`, planData);
          
          if (planData.stations && Array.isArray(planData.stations)) {
            console.log(`🏭 Found ${planData.stations.length} stations in plan ${planIndex}`);
            
            planData.stations.forEach((station, stationIndex) => {
              console.log(`🏢 Processing station ${stationIndex}:`, station);
              
              // Normalize station data format
              const normalizedStation = station.station_id || station.id || station.name;
              
              console.log(`🏷️ Station normalized: ${normalizedStation}`);
              console.log(`⏰ Station hourlyStaffing:`, station.hourlyStaffing);
              
              // 🔬 ERWEITERTE Datenanalyse - Zeige alle Stunden strukturiert
              console.log('🔬 === DETAILLIERTE STUNDEN-ANALYSE ===');
              console.log(`🔬 Station: ${normalizedStation}`);
              console.log('🔬 Frühe Stunden (7-12):');
              for (let hour = 7; hour <= 12; hour++) {
                const data = station.hourlyStaffing[hour];
                console.log(`🔬   ${hour}:00 =>`, data ? JSON.stringify(data) : 'UNDEFINED');
              }
              console.log('🔬 Mittlere Stunden (13-18):');
              for (let hour = 13; hour <= 18; hour++) {
                const data = station.hourlyStaffing[hour];
                console.log(`🔬   ${hour}:00 =>`, data ? JSON.stringify(data) : 'UNDEFINED');
              }
              console.log('🔬 Späte Stunden (19-24):');
              for (let hour = 19; hour <= 24; hour++) {
                const data = station.hourlyStaffing[hour];
                console.log(`🔬   ${hour}:00 =>`, data ? JSON.stringify(data) : 'UNDEFINED');
              }
              console.log('🔬 === ENDE DATENANALYSE ===');
              
              // Debug: Zeige die ersten paar Stunden detailliert
              console.log('🔬 Detailed hourly breakdown for station', normalizedStation + ':');
              for (let hour = 7; hour <= 12; hour++) {
                if (station.hourlyStaffing[hour]) {
                  console.log(`  Hour ${hour}:`, station.hourlyStaffing[hour]);
                }
              }
              
              // Convert to expected format for PersonalPlanningEnhanced
              const shiftData = {
                id: plan.id,
                date: plan.date,
                station: normalizedStation,
                station_id: normalizedStation,
                start_time: planData.startTime || '07:00',
                end_time: planData.endTime || '25:00',
                hourly_staffing: station.hourlyStaffing || {},
                created_at: plan.created_at,
                updated_at: plan.updated_at
              };
              
              console.log(`✅ Created shift data:`, shiftData);
              shifts.push(shiftData);
              
              // Add unique station
              if (!uniqueStations.find(s => s.id === normalizedStation)) {
                uniqueStations.push({
                  id: normalizedStation,
                  name: String(normalizedStation).toUpperCase() === 'WG'
                    ? 'Wiesn-Garten'
                    : `Station ${normalizedStation}`
                });
              }
            });
          } else {
            console.warn(`⚠️ No stations found in plan ${planIndex} data`);
          }
        } catch (parseErr) {
          console.error(`❌ Error parsing plan ${planIndex} data:`, parseErr);
        }
      });
      
      console.log('🎯 Final transformed shifts:', shifts);
      console.log('🏭 Final unique stations:', uniqueStations);
      
      // Store flexible shifts for staffing calculations
      setFlexibleShifts(shifts);
      setStations(uniqueStations);
      
      console.log('✅ loadFlexibleShifts completed successfully');
    } catch (error) {
      console.error('❌ Error in loadFlexibleShifts:', error);
      setFlexibleShifts([]);
      setStations([]);
    }
  };

  const loadPartnerPreferences = async () => {
    try {
      console.log('🤝 Loading partner preferences...');
      const data = await apiCall('/partner-preferences');
      console.log('🤝 Partner preferences loaded:', data);
      setPartnerPreferences(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fehler beim Laden der Partner-Präferenzen:', error);
      setPartnerPreferences([]);
    }
  };

  const loadStationStats = async () => {
    try {
      console.log('🏢 Loading station statistics...');
      const data = await apiCall('/users/station-stats');
      console.log('🏢 Station stats loaded:', data);
      setStationStats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Fehler beim Laden der Station-Statistiken:', error);
      setStationStats([]);
    }
  };

  const handleDrop = async (employee, station, startTime, endTime) => {
    try {
      // Prüfe, ob es sich um eine Partner-Gruppe handelt
      if (employee.type === 'group') {
        // Weise alle Mitglieder der Gruppe zu
        const assignmentPromises = employee.members.map(member => 
          apiCall('/planning/assign', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              date: selectedDate,
              user_id: member.id,
              station_id: station.toUpperCase(),
              start_time: startTime,
              end_time: endTime,
              is_cashier: member.can_work_cash_register && station.toUpperCase() === 'G1'
            }),
          })
        );

        const results = await Promise.all(assignmentPromises);
        // Korrekt synchron prüfen (async-Callback war immer truthy -> Fehler blieben unentdeckt)
        const allSuccessful = results.every((result) => result && result.success);

        if (allSuccessful) {
          await loadAssignments();
          showToast(`Partner-Team "${employee.name}" erfolgreich zugewiesen`, 'success');
        } else {
          showToast('Fehler beim Zuweisen des Partner-Teams', 'danger');
        }
      } else {
        // Einzelner Mitarbeiter
        const requestData = {
          date: selectedDate,
          user_id: employee.id,
          station_id: station.toUpperCase(), // Normalisiere Station-ID
          start_time: startTime,
          end_time: endTime,
          is_cashier: employee.can_work_cash_register && station.toUpperCase() === 'G1' // G1 ist die Kasse
        };

        const response = await apiCall('/planning/assign', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        const result = response;

        if (result.success) {
          console.log('🎯 Assignment successful, reloading assignments...');
          await loadAssignments();
          console.log('🔄 Assignments reloaded, current assignments count:', assignments.length);
          showToast('Mitarbeiter erfolgreich zugewiesen', 'success');
        } else {
          showToast('Fehler beim Zuweisen des Mitarbeiters', 'danger');
        }
      }
    } catch (error) {
      console.error('Fehler beim Zuweisen:', error);
      // z.B. 409 „Bereits verplant …" – die Server-Meldung zeigen und die Ansicht auffrischen
      showToast(error && error.message ? error.message : 'Fehler beim Zuweisen', 'danger');
      loadAssignments();
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    try {
      const result = await apiCall(`/planning/assign/${assignment.id}`, {
        method: 'DELETE'
      });

      if (result.success) {
        await loadAssignments();
        showToast('Zuweisung erfolgreich entfernt', 'success');
      } else {
        showToast('Fehler beim Entfernen der Zuweisung', 'danger');
      }
    } catch (error) {
      console.error('Fehler beim Entfernen:', error);
      showToast(/nicht gefunden/i.test(String(error && error.message)) ? 'Dieser Einsatz war schon entfernt – Ansicht wurde aktualisiert.' : 'Fehler beim Entfernen der Zuweisung', 'danger');
      loadAssignments();
    }
  };

  const handleUpdateAssignment = async (assignmentId, updates) => {
    try {
      const response = await apiCall(`/planning/assign/${assignmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const result = response;

      if (result.success) {
        await loadAssignments();
        showToast('Arbeitszeit aktualisiert', 'success');
      } else {
        showToast('Fehler beim Aktualisieren', 'danger');
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      // 404 = Einsatz gibt es nicht mehr (inzwischen abgesagt/verschoben, z.B. per WhatsApp) → Ansicht war veraltet
      showToast(/nicht gefunden/i.test(String(error && error.message)) ? 'Dieser Einsatz existiert nicht mehr (inzwischen abgesagt oder geändert) – Ansicht wurde aktualisiert.' : (error.message || 'Fehler beim Aktualisieren'), 'danger');
      loadAssignments();
    }
  };

  const handleToggleLockAssignment = async (assignmentId, lockState) => {
    try {
      const response = await apiCall(`/planning/assign/${assignmentId}/lock`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ locked: lockState }),
      });

      const result = response;

      if (result.success) {
        await loadAssignments();
        showToast(
          lockState ? 'Zuweisung gesperrt' : 'Zuweisung entsperrt', 
          'success'
        );
      } else {
        showToast('Fehler beim Ändern des Lock-Status', 'danger');
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Lock-Status:', error);
      showToast('Fehler beim Ändern des Lock-Status', 'danger');
    }
  };

  const handleSuggestionAssign = (employee, station, time) => {
    handleDrop(employee, station, time);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="container-fluid mt-4">
        {/* Toast */}
        {toast && (
          <div className={`alert alert-${toast.type} alert-dismissible fade show position-fixed`} 
               style={{ top: '20px', right: '20px', zIndex: 1050 }}>
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h1 className="h3 mb-0">Schichtzuteilung</h1>
                    <small className="text-muted">Konkrete Mitarbeiter per Drag &amp; Drop auf Stationen/Zeiten zuteilen. Den Stundenbedarf legst du unter „Bedarfsplanung" fest.</small>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <label className="form-label mb-0">Datum:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      style={{ width: '160px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hinweise, Nachrichten, Änderungen, Planvorschlag … (eigene Komponente, siehe components/PlanningInsights) */}
        <PlanningInsights date={selectedDate} user={user} refreshKey={assignments} onChanged={loadAssignments} />

        {/* Main Content */}
        <div className="row g-0">
          {/* Employee Containers – angepinnt: bleiben beim Scrollen zu unteren
              Stationen (z.B. Wiesn-Garten) sichtbar, scrollen bei Platzmangel
              intern. Sonst wäre Drag & Drop nach unten nur auf Riesen-Monitoren
              möglich. */}
          <div
            className="col-md-3"
            style={{
              position: 'sticky',
              top: '8px',
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 16px)',
              overflowY: 'auto'
            }}
          >
            {/* Meldungen auf freie Schichten (Schichtbörse) – nur für diesen Tag */}
            {claimsToday.length > 0 && (
              <div className="card mb-3 border-warning" style={{ borderWidth: 2 }}>
                <div className="card-header bg-warning bg-opacity-25 py-2">
                  <h6 className="mb-0">🙋 Meldungen auf freie Schichten</h6>
                  <small className="text-muted">Vorläufig – erst dein ✓ macht daraus eine Schicht</small>
                </div>
                <div className="card-body p-2">
                  {claimsToday.map((o) => (
                    <div key={o.id} className="mb-2">
                      <div className="small fw-bold">
                        {String(o.from).padStart(2, '0')}:00–{String(o.to === 25 ? 1 : o.to).padStart(2, '0')}:00 · {o.station}
                        {o.needed > 1 && <span className="badge bg-secondary ms-1">{o.needed} Plätze</span>}
                      </div>
                      {o.claims.map((c) => (
                        <div key={c.claim_id} className="d-flex align-items-center justify-content-between border rounded px-2 py-1 mt-1 bg-warning bg-opacity-10">
                          <div className="small">
                            {c.name}{c.kasse ? ' 💰' : ''}
                            <span className="text-muted"> · {c.shifts} Schichten{c.source === 'whatsapp' ? ' · 📱' : ''}</span>
                          </div>
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-success py-0 px-2" title="Einplanen und Mitarbeiter benachrichtigen"
                              onClick={() => decideClaim(c, o, true)}>✓</button>
                            <button className="btn btn-outline-secondary py-0 px-2" title="Meldung ablehnen – mit Begründung"
                              onClick={() => setDeclineBox({ claim: c, offer: o })}>✗</button>
                          </div>
                        </div>
                      ))}
                      {o.declined.length > 0 && (
                        <div className="small text-muted mt-1">
                          ✗ abgesagt: {o.declined.map((d) => d.name).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Employees */}
            <div className="card mb-3">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-0">Verfügbare Mitarbeiter</h6>
                    <small className="text-muted">({displayItems.filter(item => {
                      if (item.type === 'group') {
                        return !item.members.some(member => assignments.some(a => a.user_id === member.id));
                      } else {
                        return !assignments.some(a => a.user_id === item.id);
                      }
                    }).length})</small>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {/* Partner-Gruppen Legende */}
                    <div className="dropdown">
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        type="button" 
                        data-bs-toggle="dropdown"
                        title="Partner-Gruppierungen Legende"
                      >
                        🤝
                      </button>
                      <div className="dropdown-menu dropdown-menu-end p-3" style={{ minWidth: '280px' }}>
                        <h6 className="dropdown-header">Partner-Gruppierungen</h6>
                        <div className="mb-2">
                          <span className="badge bg-primary me-2">🤝</span>
                          <small>Aktive Partner-Teams (beide verfügbar)</small>
                        </div>
                        <div className="mb-2">
                          <span className="badge bg-secondary me-2">💭</span>
                          <small>Hat Partner-Wünsche (Partner nicht verfügbar)</small>
                        </div>
                        <div className="mb-2">
                          <span className="badge bg-success me-2">💰</span>
                          <small>Kann Kasse bedienen</small>
                        </div>
                        <hr className="my-2" />
                        <small className="text-muted">
                          Partner-Teams werden in farbigen Containern zusammen angezeigt.
                          Einzelne Mitarbeiter mit Partner-Wünschen erhalten ein 💭 Symbol.
                        </small>
                      </div>
                    </div>
                    <div className="form-check form-check-sm">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="cashierFilter"
                        checked={showOnlyCashiers}
                        onChange={(e) => setShowOnlyCashiers(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="cashierFilter" title="Nur Kassenkräfte anzeigen">
                        💰 Nur Kasse
                      </label>
                    </div>
                    <button
                      type="button"
                      className={`btn btn-sm ${sortByFairness ? 'btn-warning' : 'btn-outline-secondary'}`}
                      onClick={() => setSortByFairness(!sortByFairness)}
                      title="Nach Fairness sortieren: die am stärksten Benachteiligten zuerst"
                    >
                      ⚖️
                    </button>
                  </div>
                </div>
                {/* Search field for available employees */}
                <div className="p-2 border-bottom">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="🔍 Mitarbeiter suchen..."
                    value={availableSearch}
                    onChange={(e) => setAvailableSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {loading ? (
                  <div className="text-center p-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Laden...</span>
                    </div>
                  </div>
                ) : (
                  filterBySearch(displayItems, availableSearch)
                    .filter(item => {
                      if (item.type === 'group') {
                        // Gruppe ist verfügbar, wenn nicht alle Mitglieder geplant sind
                        return !item.members.every(member => assignments.some(a => a.user_id === member.id));
                      } else {
                        // Einzelperson ist verfügbar, wenn nicht geplant
                        return !assignments.some(a => a.user_id === item.id);
                      }
                    })
                    .map(item => 
                      item.type === 'group' ? (
                        <PartnerGroupContainer
                          key={item.id}
                          group={item}
                          suggestions={suggestions}
                          onAssign={handleSuggestionAssign}
                          availabilityData={availabilityData}
                          plannedAssignments={assignments}
                          isPlanned={false}
                          stations={stations}
                          highlightedEmployeeId={highlightedEmployeeId}
                          onEmployeeClick={setHighlightedEmployeeId}
                          stationStats={stationStats}
                          prevAssignmentsByUser={prevAssignmentsByUser}
                          fairnessByUser={fairnessByUser}
                        />
                      ) : (
                        <EmployeeCard
                          key={item.id}
                          employee={item}
                          suggestions={suggestions}
                          onAssign={handleSuggestionAssign}
                          availabilityData={availabilityData}
                          plannedAssignments={assignments}
                          isPlanned={false}
                          stations={stations}
                          highlightedEmployeeId={highlightedEmployeeId}
                          onEmployeeClick={setHighlightedEmployeeId}
                          isPartnerMember={false}
                          stationStats={stationStats}
                          prevAssignmentsByUser={prevAssignmentsByUser}
                          fairnessByUser={fairnessByUser}
                          partnerGroupColor={(() => {
                            // Prüfe, ob dieser Mitarbeiter potenzielle Partner hat (aber nicht verfügbar ist)
                            const hasPartnerWishes = partnerPreferences.some(pref => 
                              pref.user_id === item.id || pref.preferred_partner_id === item.id
                            );
                            
                            if (hasPartnerWishes) {
                              // Verwende eine gedämpfte Farbe für potenzielle Partner
                              return {
                                bg: '#f8f9fa',
                                border: '#6c757d',
                                accent: '#6c757d'
                              };
                            }
                            return null;
                          })()}
                          hasPotentialPartners={partnerPreferences.some(pref => 
                            pref.user_id === item.id || pref.preferred_partner_id === item.id
                          )}
                        />
                      )
                    )
                )}
                {displayItems.filter(item => {
                  if (item.type === 'group') {
                    return !item.members.every(member => assignments.some(a => a.user_id === member.id));
                  } else {
                    return !assignments.some(a => a.user_id === item.id);
                  }
                }).length === 0 && !loading && (
                  <div className="text-center p-2 text-muted">
                    <small>Alle Mitarbeiter geplant</small>
                  </div>
                )}
              </div>
            </div>

            {/* Planned Employees */}
            <div className="card">
              <div className="card-header">
                <h6>Geplante Mitarbeiter</h6>
                <small className="text-muted">({displayItems.filter(item => {
                  if (item.type === 'group') {
                    return item.members.some(member => assignments.some(a => a.user_id === member.id));
                  } else {
                    return assignments.some(a => a.user_id === item.id);
                  }
                }).length})</small>
                {/* Search field for planned employees */}
                <div className="p-2 border-top">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="🔍 Geplante Mitarbeiter suchen..."
                    value={plannedSearch}
                    onChange={(e) => setPlannedSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filterBySearch(displayItems, plannedSearch)
                  .filter(item => {
                    if (item.type === 'group') {
                      return item.members.some(member => assignments.some(a => a.user_id === member.id));
                    } else {
                      return assignments.some(a => a.user_id === item.id);
                    }
                  })
                  .map(item => 
                    item.type === 'group' ? (
                      <PartnerGroupContainer
                        key={item.id}
                        group={item}
                        suggestions={suggestions}
                        onAssign={handleSuggestionAssign}
                        availabilityData={availabilityData}
                        plannedAssignments={assignments}
                        isPlanned={true}
                        stations={stations}
                        highlightedEmployeeId={highlightedEmployeeId}
                        onEmployeeClick={setHighlightedEmployeeId}
                        stationStats={stationStats}
                        prevAssignmentsByUser={prevAssignmentsByUser}
                          fairnessByUser={fairnessByUser}
                      />
                    ) : (
                      <EmployeeCard
                        key={item.id}
                        employee={item}
                        suggestions={suggestions}
                        onAssign={handleSuggestionAssign}
                        availabilityData={availabilityData}
                        plannedAssignments={assignments}
                        isPlanned={true}
                        stations={stations}
                        highlightedEmployeeId={highlightedEmployeeId}
                        onEmployeeClick={setHighlightedEmployeeId}
                        stationStats={stationStats}
                        prevAssignmentsByUser={prevAssignmentsByUser}
                          fairnessByUser={fairnessByUser}
                      />
                    )
                  )
                }
                {displayItems.filter(item => {
                  if (item.type === 'group') {
                    return item.members.some(member => assignments.some(a => a.user_id === member.id));
                  } else {
                    return assignments.some(a => a.user_id === item.id);
                  }
                }).length === 0 && (
                  <div className="text-center p-2 text-muted">
                    <small>Noch keine Mitarbeiter geplant</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timeline Schedule */}
          <div className="col-md-9">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Schichtplan für {selectedDate}</h5>
                  <span className="badge bg-secondary">Timeline-Ansicht mit Resize</span>
                </div>
              </div>
              <div className="card-body" style={{ overflowX: 'auto' }}>
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Laden...</span>
                    </div>
                    <div className="mt-2">Lade Schichtplan...</div>
                  </div>
                ) : (
                  <div>
                    {(stations || []).map(station => (
                      <StationTimeline
                        key={station.id}
                        station={station.id}
                        assignments={assignments}
                        onDrop={handleDrop}
                        onRemove={handleRemoveAssignment}
                        onUpdateAssignment={handleUpdateAssignment}
                        onToggleLockAssignment={handleToggleLockAssignment}
                        availabilityData={availabilityData}
                        flexibleShifts={flexibleShifts}
                        selectedDate={selectedDate}
                        highlightedEmployeeId={highlightedEmployeeId}
                        onHighlight={setHighlightedEmployeeId}
                        getEmployeeColor={getEmployeeColor}
                        cashierAssignments={cashierAssignments}
                        getAvailableCashiers={getAvailableCashiers}
                        assignCashier={assignCashier}
                        employees={employees}
                        timeToMinutes={timeToMinutes}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="row mt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6>Legende - Resizable Timeline:</h6>
                <div className="row">
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="bg-primary text-white px-2 py-1 rounded me-2" style={{ fontSize: '12px' }}>
                        👤 Name
                      </div>
                      <small>Normale Mitarbeiter (resizable)</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="bg-success text-white px-2 py-1 rounded border border-warning border-2 me-2" style={{ fontSize: '12px' }}>
                        💰 Name
                      </div>
                      <small>Kassenkraft (resizable)</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-arrows-move me-2 text-primary"></i>
                      <small>Ganzen Balken ziehen = Verschieben</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-arrow-left-right me-2 text-warning"></i>
                      <small>Ränder ziehen = Start/End ändern</small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-clock me-2 text-success"></i>
                      <small>Drop = 8h Standard</small>
                    </div>
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-12">
                    <small className="text-muted">
                      <strong>Funktionen:</strong> Drop zur genauen Zeit = 8h Schicht (max. Verfügbarkeit). 
                      Ganzen Balken ziehen = Verschieben. Ränder ziehen = Start/Endzeit ändern (30-Min-Schritte). 
                      Kassenkräfte sind grün mit gelbem Rahmen. Intelligente Zeilen-Zuordnung.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {declineBox && (
        <ClaimDeclineModal key={declineBox.claim.claim_id} box={declineBox}
          onClose={() => setDeclineBox(null)}
          onConfirm={(note, send) => decideClaim(declineBox.claim, declineBox.offer, false, note, send)} />
      )}
    </DndProvider>
  );
}

export default PersonalPlanningEnhanced;
